// added-video-player
import React, { useEffect, useState, useRef, FC } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Box,
  CircularProgress,
  Alert,
  TableHead,
  Typography,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  Link,
  Autocomplete,
  LinearProgress,
  DialogContentText,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { sheetsService } from '../services/googleSheetsService';
import { useNavigate, useLocation } from 'react-router-dom';
import { youtubeService } from '../services/youtubeService';
import { ScriptManager } from './ScriptManager';
import { YouTubeSearchDialog } from './YouTubeSearchDialog';
import SearchIcon from '@mui/icons-material/Search';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'; // Added CloudDownloadIcon
import { useSnackbar } from 'notistack';

const SPREADSHEET_ID = '1aVv8xC1HIB1XQDDwF8wiLtkB9c-D_g4zPnhFhbizkTo';
const HEADER_ROW = 1;
const FIRST_DATA_ROW = 2;

interface Cell {
  value: string | undefined;
  isEditing: boolean;
}

interface Row {
  cells: Cell[];
  isNew: boolean;
}

interface ParsedScript {
  intro: string;
  songs: string[];
  outro: string;
}

export const VideoManager: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'song', direction: 'asc' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<number | null>(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [cellToFocus, setCellToFocus] = useState<{row: number, col: number} | null>(null);
  const [videoPlayerDialogOpen, setVideoPlayerDialogOpen] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState('');
  const [currentInPoint, setCurrentInPoint] = useState('0');
  const [currentSeekIn, setCurrentSeekIn] = useState('0');
  const [currentDuration, setCurrentDuration] = useState('0'); // Add state for duration
  const [currentRowIndex, setCurrentRowIndex] = useState(-1);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewTimeout, setPreviewTimeout] = useState<NodeJS.Timeout | null>(null);
  const [scriptDialogOpen, setScriptDialogOpen] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [searchInitialQuery, setSearchInitialQuery] = useState('');
  const [searchTargetRowIndex, setSearchTargetRowIndex] = useState<number | null>(null);
  const [highlightedRow, setHighlightedRow] = useState<number | null>(null);
  const highlightedRowRef = useRef<HTMLTableRowElement>(null);
  const [editingS3UrlState, setEditingS3UrlState] = useState<{ row: number; col: number; url: string; song?: string; artist?: string; currentYoutubeUrl?: string; } | null>(null); // State for S3 URL edit dialog
  const [openedFromCharts, setOpenedFromCharts] = useState(false); // Track if opened from charts
  const [editingVerticalUrlState, setEditingVerticalUrlState] = useState<{ row: number; col: number; url: string; song?: string; artist?: string } | null>(null); // Add state for Vertical URL edit dialog
  const [verticalVideoUploadFile, setVerticalVideoUploadFile] = useState<File | null>(null); // State for file upload
  const [verticalUploadProgress, setVerticalUploadProgress] = useState<number>(0); // Upload progress
  // Add a state to track video loading status
  const [videoLoadError, setVideoLoadError] = useState<boolean>(false);
  const [videoSignedUrl, setVideoSignedUrl] = useState<string>(''); // Add this state for the signed URL
  const [isLoadingSignedUrl, setIsLoadingSignedUrl] = useState<boolean>(false); // Add this state to track loading
  // Add state variables for S3 video upload (next to the vertical video upload state variables)
  const [s3VideoUploadFile, setS3VideoUploadFile] = useState<File | null>(null); // State for S3 file upload
  const [s3UploadProgress, setS3UploadProgress] = useState<number>(0); // Upload progress for S3

  // State for filename confirmation dialog
  const [isFileNameConfirmationDialogOpen, setIsFileNameConfirmationDialogOpen] = useState(false);
  const [pendingUploadFile, setPendingUploadFile] = useState<File | null>(null);
  const [currentUploadFileName, setCurrentUploadFileName] = useState<string>('');
  const [uploadTargetRowInfo, setUploadTargetRowInfo] = useState<{ rowIndex: number; s3Key?: string } | null>(null);
  const [isUploadingS3, setIsUploadingS3] = useState<boolean>(false); // New state for upload in progress
  const [dialogInputYoutubeUrl, setDialogInputYoutubeUrl] = useState<string>(''); // State for YouTube URL input in S3 dialog
  // const [isS3DialogReadyForAction, setIsS3DialogReadyForAction] = useState<boolean>(false); // Flag for S3 dialog actions - REMOVED

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    console.log(`[VideoManager] videoPlayerDialogOpen state changed to: ${videoPlayerDialogOpen}`);
  }, [videoPlayerDialogOpen]);

  useEffect(() => {
    if (!rows.length || !headers.length) return;

    const params = new URLSearchParams(location.search);
    const songParam = params.get('song');
    const artistParam = params.get('artist');
    const playerParam = params.get('player');
    const s3UrlParam = params.get('s3url'); // Get S3 URL from params
    const hasPlayerParam = playerParam === 'true'; // Define hasPlayerParam from playerParam

    console.log(`[VideoManager] URL Params Check - song: "${songParam}", artist: "${artistParam}", player: "${playerParam}", s3url: "${s3UrlParam}"`);
    console.log(`[VideoManager] Available headers:`, headers);

    // Check if hasPlayerParam is true before proceeding with player logic
    if (hasPlayerParam) {
      if (s3UrlParam && !videoPlayerDialogOpen) {
        // Logic for direct S3 URL
        console.log(`[VideoManager] Direct S3 URL provided: "${s3UrlParam}"`);
        // Find In Point from params or default
        const inPointParam = params.get('in_point') || '0'; // Assuming in_point might be passed

        // Set state and open player
        setCurrentVideoUrl(s3UrlParam);
        setCurrentInPoint(inPointParam);
        setCurrentDuration('0'); // Default Duration
        setCurrentRowIndex(-1); // Indicate we didn't necessarily find a row index

        setTimeout(() => {
            console.log('[VideoManager] Opening video player dialog NOW with direct S3 URL:', s3UrlParam);
            setVideoPlayerDialogOpen(true);
            // Clear URL parameters after opening dialog
            setTimeout(() => {
                console.log('[VideoManager] Clearing URL parameters (direct S3 URL)');
                navigate('/video-manager', { replace: true });
            }, 200);
        }, 100);

        return; // Stop further processing if we handled direct S3 URL
      } else if (songParam && artistParam && !videoPlayerDialogOpen) {
        // Logic for finding row by song/artist
        console.log(`[VideoManager] Looking for song: "${songParam}" by artist: "${artistParam}"`);

        const songColumnIndex = headers.findIndex(h => h.toLowerCase().includes('song'));
        const artistColumnIndex = headers.findIndex(h => h.toLowerCase().includes('artist'));

        if (songColumnIndex === -1 || artistColumnIndex === -1) {
          console.warn('[VideoManager] Cannot locate song/artist columns');
          navigate('/video-manager', { replace: true }); // Clear params if columns missing
          return;
        }

        console.log(`[VideoManager] Song column index: ${songColumnIndex}, Artist column index: ${artistColumnIndex}`);

        const matchingRowIndex = rows.findIndex(row => {
          if (row.isNew) return false;
          const rowSong = (row.cells[songColumnIndex]?.value || '').toLowerCase().trim();
          const rowArtist = (row.cells[artistColumnIndex]?.value || '').toLowerCase().trim();
          const paramSong = songParam.toLowerCase().trim();
          const paramArtist = artistParam.toLowerCase().trim();
          const songMatch = rowSong.includes(paramSong) || paramSong.includes(rowSong);
          const artistMatch = rowArtist.includes(paramArtist) || paramArtist.includes(rowArtist);
          console.log(`[VideoManager] Comparing - Row: "${rowSong}" / "${rowArtist}" to Param: "${paramSong}" / "${paramArtist}" - Match: ${songMatch && artistMatch}`);
          return songMatch && artistMatch;
        });

        if (matchingRowIndex !== -1) {
          console.log(`[VideoManager] Found matching row at index ${matchingRowIndex}`);
          setHighlightedRow(matchingRowIndex);

          const s3LinkColIndex = headers.findIndex(h => h.toLowerCase().includes('s3 url'));
          console.log(`[VideoManager] S3 URL column index: ${s3LinkColIndex}`);

          if (s3LinkColIndex !== -1) {
            const s3Url = rows[matchingRowIndex].cells[s3LinkColIndex]?.value || '';
            console.log(`[VideoManager] S3 URL found: "${s3Url}"`);

            if (s3Url) {
              console.log('[VideoManager] Setting current row and S3 video URL');
              const inPointColIndex = headers.findIndex(h => h.toLowerCase().includes('in point'));
              const durationColIndex = headers.findIndex(h => h.toLowerCase().includes('duration'));
              const inPointValue = inPointColIndex !== -1 ? rows[matchingRowIndex].cells[inPointColIndex]?.value || '0' : '0';
              const durationValue = durationColIndex !== -1 ? rows[matchingRowIndex].cells[durationColIndex]?.value || '0' : '0'; // Get duration

              setTimeout(() => {
                setCurrentRowIndex(matchingRowIndex); // Set the found index
                setCurrentVideoUrl(s3Url);
                setCurrentInPoint(inPointValue);
                setCurrentDuration(durationValue); // Set duration state

                setTimeout(() => {
                  console.log('[VideoManager] Opening video player dialog NOW with S3 URL:', s3Url);
                  setVideoPlayerDialogOpen(true);
                  setTimeout(() => {
                    console.log('[VideoManager] Clearing URL parameters (found S3 URL)');
                    navigate('/video-manager', { replace: true });
                  }, 200);
                }, 100);
              }, 100);
            } else {
              console.warn('[VideoManager] No S3 URL found for the matching row');
              navigate('/video-manager', { replace: true });
            }
          } else {
            console.warn('[VideoManager] S3 Link column not found');
            navigate('/video-manager', { replace: true });
          }
        } else {
          console.log(`[VideoManager] No matching row found for "${songParam}" by "${artistParam}"`);
          navigate('/video-manager', { replace: true });
        }
      }
    } else if (location.search) {
      // If playerParam is not true or other params exist without player=true
      console.log('[VideoManager] Clearing non-player or other URL parameters');
      navigate('/video-manager', { replace: true });
    }
  }, [rows, headers, location.search, navigate, videoPlayerDialogOpen]); // Add videoPlayerDialogOpen dependency

  useEffect(() => {
    if (highlightedRow !== null && highlightedRowRef.current) {
      highlightedRowRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      const timer = setTimeout(() => {
        setHighlightedRow(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [highlightedRow]);

  useEffect(() => {
    if (cellToFocus) {
      const input = document.querySelector(`[data-row="${cellToFocus.row}"][data-col="${cellToFocus.col}"] input`);
      if (input) {
        (input as HTMLInputElement).focus();
        setCellToFocus(null);
      }
    }
  }, [cellToFocus]); // Added missing dependency

  useEffect(() => {
    if (editingS3UrlState && !editingS3UrlState.currentYoutubeUrl) {
      setDialogInputYoutubeUrl(''); // Reset when dialog opens and no initial YT URL
    }
  }, [editingS3UrlState]);

  // --- Define Handlers First ---
  const handleMarkInPoint = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    if (!isPreviewing) {
      const video = e.target as HTMLVideoElement;
      const newInPoint = video.currentTime.toFixed(1);
      setCurrentInPoint(newInPoint);
      if (currentRowIndex !== -1) {
        const inPointColIndex = headers.findIndex(h => h.toLowerCase().includes('in point'));
        if (inPointColIndex !== -1) {
          setRows(prevRows => {
            const newRows = [...prevRows];
            const dataRowIndex = currentRowIndex;
            if (newRows[dataRowIndex]?.cells[inPointColIndex]) {
              newRows[dataRowIndex].cells[inPointColIndex].value = newInPoint;
            }
            return newRows;
          });
      }
      }
    }
  };

  const handleInPointChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Corrected regex: use single backslashes for RegExp literals
    if (value === '' || /^\d*\.?\d{0,1}$/.test(value)) { 
      setCurrentInPoint(value); // Update local state for the dialog field
      // Do NOT directly update rows state here, wait for Save button
    }
  };

  const handleSeekInChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Corrected regex: use single backslashes for RegExp literals
    if (value === '' || /^\d*\.?\d{0,1}$/.test(value)) { 
      setCurrentSeekIn(value); // Update local state for the dialog field
      // Do NOT directly update rows state here, wait for Save button
    }
  };

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Corrected regex: use single backslashes for RegExp literals
    if (value === '' || /^\d*\.?\d{0,1}$/.test(value)) { 
      setCurrentDuration(value); // Update local state for the dialog field
      // Do NOT directly update rows state here, wait for Save button
    }
  };

  const handleScriptSave = (parsedScript: ParsedScript) => {
    console.log('[VideoManager] Script saved (not implemented):', parsedScript);
    setScriptDialogOpen(false);
  };
  // --- End Define Handlers ---


  // Function to load data from the spreadsheet
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await sheetsService.getData(SPREADSHEET_ID, 'Sheet1!A:Z'); 
      console.log('[VideoManager] Fetched data response:', response); 
      
      if (!response || !response.values || response.values.length === 0) {
        console.warn('[VideoManager] No data or empty sheet found.');
        setHeaders([]);
        setRows([]);
        return; 
      }

      const fetchedHeaders = response.values[0].map((header: any) => String(header));
      console.log('[DEBUG] VideoManager headers loaded:', fetchedHeaders); 
      console.log('[DEBUG] Zoom header present:', fetchedHeaders.some(h => h.toLowerCase().includes('zoom'))); // Added log for Zoom header
      console.log('[DEBUG] Looking for column containing "vertical url":', fetchedHeaders.find(h => h.toLowerCase().includes('vertical url'))); 

      if (fetchedHeaders.length === 0) {
        console.warn('[VideoManager] No headers found in sheet.');
        setHeaders([]);
        setRows([]);
        return;
      }

      // Find column indices once
      // const songColIndex = fetchedHeaders.findIndex(h => h.toLowerCase().includes('song')); // Removed unused variable
      // const s3ColIndex = fetchedHeaders.findIndex(h => h.toLowerCase().includes('s3 url')); // Removed unused variable

      const processedDataRows: Row[] = response.values.slice(1).map((row: any[], rowIndex: number): Row => { 
        const cells: Cell[] = fetchedHeaders.map((header, index) => {
          const cellValue = index < row.length ? String(row[index] ?? '').trim() : '';
          
          return {
            value: cellValue, 
              isEditing: false
          };
        });

        // Ensure cell count matches header count
        while (cells.length < fetchedHeaders.length) {
          cells.push({ value: '', isEditing: false });
        }
        
            return {
              cells,
              isNew: false
            };
          });
        
      const newBlankRow: Row = {
        cells: fetchedHeaders.map(() => ({ value: '', isEditing: false })),
          isNew: true
        };
        
      setHeaders(fetchedHeaders);
      setRows([newBlankRow, ...processedDataRows]); 

    } catch (error: unknown) { // Type the error
      console.error('[VideoManager] Detailed error in loadData:', error);
      setError(`Failed to load data: ${error instanceof Error ? error.message : 'Unknown error'}`);
        setHeaders([]);
        setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async () => {
    try {
      await sheetsService.signIn();
      setIsSignedIn(true);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign in');
      setIsSignedIn(false);
    }
  };

  const handleCellEdit = (rowIndex: number, colIndex: number) => {
    // Never allow S3 URL column to be edited
    if (headers[colIndex]?.toLowerCase().includes('s3 url')) {
      console.log('S3 URL cell edit prevented');
      return;
    }
    
    // Skip S3 URL column completely to prevent any editing
    if (colIndex >= 0 && headers[colIndex]?.toLowerCase().includes('s3 url')) {
      return; // Do nothing for S3 URL cells
    }
    console.log(`[DEBUG] handleCellEdit called for row ${rowIndex}, col ${colIndex}. Header: ${headers[colIndex]}`); // Added log

    // Set only the clicked cell to editing mode
    setRows(prevRows => {
      const newRows = [...prevRows];
      // First, set all cells to not editing
      newRows.forEach(row => {
        row.cells.forEach(cell => {
          cell.isEditing = false;
        });
      });
      // Then set the clicked cell to editing
      newRows[rowIndex].cells[colIndex].isEditing = true;
      console.log(`[DEBUG] setRows in handleCellEdit: for row ${rowIndex}, col ${colIndex}, isEditing is now ${newRows[rowIndex].cells[colIndex].isEditing}`); // Added log
      return newRows;
    });
  };

  const handleCellChange = async (rowIndex: number, colIndex: number, value: string) => {
    console.log(`[DEBUG] handleCellChange called for row ${rowIndex}, col ${colIndex}, value: ${value}. Header: ${headers[colIndex]}`); // Added log

    // Update local React state for the cell's value.
    // If it's the YouTube link in the new row, also try to fetch and set song/artist.
    if (rowIndex === 0 && headers[colIndex].toLowerCase().includes('youtube')) {
      try {
        const songInfo = await youtubeService.getSongInfo(value);
        // Find the indices for song and artist columns using `find` to get the header string, then `indexOf`
        const songColHeader = headers.find(h => h.toLowerCase().includes('song'));
        const artistColHeader = headers.find(h => h.toLowerCase().includes('artist'));

        setRows(prevRows => {
          const newRows = [...prevRows];
          // Ensure the cells array for the current row exists
          if (!newRows[rowIndex] || !newRows[rowIndex].cells) {
            console.error(`[VideoManager] New row ${rowIndex} or its cells not found during YouTube link update.`);
            return prevRows; // or handle error appropriately
          }
          const currentRowCells = newRows[rowIndex].cells;

          // Update the YouTube link cell itself
          if (currentRowCells[colIndex]) {
            currentRowCells[colIndex].value = value;
          }

          // Update song and artist if columns exist
          if (songColHeader) {
            const songColIdx = headers.indexOf(songColHeader);
            if (songColIdx !== -1 && currentRowCells[songColIdx]) {
              currentRowCells[songColIdx].value = songInfo.song;
            }
          }
          if (artistColHeader) {
            const artistColIdx = headers.indexOf(artistColHeader);
            if (artistColIdx !== -1 && currentRowCells[artistColIdx]) {
              currentRowCells[artistColIdx].value = songInfo.artist;
            }
          }
          return newRows;
        });
      } catch (error) {
        console.error('Error extracting song info for new row, still updating YouTube URL:', error);
        setRows(prevRows => {
          const newRows = [...prevRows];
          if (newRows[rowIndex] && newRows[rowIndex].cells && newRows[rowIndex].cells[colIndex]) {
            newRows[rowIndex].cells[colIndex].value = value; // Update only the YouTube URL cell
          }
          return newRows;
        });
      }
    } else {
      // Normal cell value update for all other cases (including checkboxes on existing rows)
      setRows(prevRows => {
        const newRows = [...prevRows];
        if (newRows[rowIndex] && newRows[rowIndex].cells && newRows[rowIndex].cells[colIndex]) {
          newRows[rowIndex].cells[colIndex].value = value;
        }
        return newRows;
      });
    }

    // AFTER local state for the value is updated:
    // For existing data rows (not the "new row" at index 0),
    // if the changed cell is a 'zoom' or 'video' (checkbox type),
    // save the change to Google Sheets immediately.
    const columnType = headers[colIndex]?.toLowerCase() || '';
    if (rowIndex !== 0 && (columnType.includes('zoom') || columnType.includes('video'))) {
      console.log(`[DEBUG] Checkbox change for existing row ${rowIndex} (UI index), col ${colIndex} (${headers[colIndex]}). Saving immediately with value: "${value}"`);
      // Pass the new value directly to handleCellSave.
      // handleCellSave will update the sheet and then set isEditing to false.
      await handleCellSave(rowIndex, colIndex, value);
    }
  };

  const handleCellSave = async (rowIndex: number, colIndex: number, valueToSave?: string) => {
    // Ensure we have the latest row data before saving
    const currentRows = [...rows]; // Get a snapshot
    const row = currentRows[rowIndex];
    if (!row) {
       console.error(`[handleCellSave] Row index ${rowIndex} not found.`);
       return;
    }
    const cell = row.cells[colIndex];
    if (!cell) {
        console.error(`[handleCellSave] Cell index ${colIndex} not found for row ${rowIndex}.`);
        return;
    }
    // Use provided valueToSave if available, otherwise use current cell value from state
    const finalValueToSave = typeof valueToSave !== 'undefined' ? valueToSave : cell.value;
    console.log(`[DEBUG] handleCellSave called for row ${rowIndex}, col ${colIndex}. Header: ${headers[colIndex]}. Attempting to save value: "${finalValueToSave}"`); // Added log

    try {
      // Special handling for new rows (index 0) - just update local editing state
      if (row.isNew) {
        setRows(prevRows => {
          const newRows = [...prevRows];
          if (newRows[rowIndex] && newRows[rowIndex].cells[colIndex]) {
             newRows[rowIndex].cells[colIndex].isEditing = false;
          } else {
             console.error(`[handleCellSave] New Row: Row or cell index out of bounds during state update.`);
          }
          return newRows;
        });
        return; // Don't save new row cell individually or fetch info yet
      }

      // --- For existing rows, update the cell in Google Sheets ---
      console.log(`[handleCellSave] Saving existing row ${rowIndex}, col ${colIndex}`);
      // Fix: Correctly map UI row index to Google Sheets row number
      // The first data row is at Google Sheets row 2, and rowIndex 1 in our UI
      // So we add 1 to the rowIndex, but also need to handle the fact that index 0 is our "new row"
      const sheetRow = rowIndex === 0 ? FIRST_DATA_ROW : rowIndex + 1;
      console.log(`[handleCellSave] Mapped UI rowIndex ${rowIndex} to Sheet row ${sheetRow}`);
      
      const range = `Sheet1!${String.fromCharCode(65 + colIndex)}${sheetRow}`;
      await sheetsService.updateData(SPREADSHEET_ID, range, [[finalValueToSave]]);
      console.log(`[handleCellSave] Saved to Sheet: ${range} = ${finalValueToSave}`);

      // Update local state to mark as not editing
      setRows(prevRows => {
        const newRows = [...prevRows];
         if (newRows[rowIndex] && newRows[rowIndex].cells[colIndex]) {
           newRows[rowIndex].cells[colIndex].isEditing = false;
         } else {
            console.error(`[handleCellSave] Existing Row: Row or cell index out of bounds during state update.`);
         }
        return newRows;
      });

      // --- Check if the YouTube link was updated and fetch info ---
      const isYouTubeLinkColumn = headers[colIndex]?.toLowerCase().includes('youtube link');
      if (isYouTubeLinkColumn && finalValueToSave) {
         console.log(`[handleCellSave] YouTube link saved for row ${rowIndex}, triggering info fetch...`);
         // Use the captured cellValue
         await fetchAndUpdateSongArtist(rowIndex, finalValueToSave);
      }

    } catch (err) {
      console.error(`[handleCellSave] Error saving cell for row ${rowIndex}, col ${colIndex}:`, err);
      setError('Failed to save changes');
    } finally {
      // Optional: Ensure editing is off regardless of success/failure for existing rows
      // if (rowIndex !== 0) { ... }
    }
  };

  const handleNewRowSave = async (rowIndex: number) => {
    if (!rows[rowIndex].isNew) return;

    // Prepare the values to be saved
    const values = rows[rowIndex].cells.map(cell => cell.value);

    try {
      // --- Check for YouTube URL and fetch info BEFORE saving ---
      const youtubeColIndex = headers.findIndex(h => h.toLowerCase().includes('youtube link'));
      const songColIndex = headers.findIndex(h => h.toLowerCase().includes('song'));
      const artistColIndex = headers.findIndex(h => h.toLowerCase().includes('artist'));

      if (youtubeColIndex !== -1 && songColIndex !== -1 && artistColIndex !== -1) {
        const youtubeUrl = values[youtubeColIndex];
        if (youtubeUrl) {
          console.log('[handleNewRowSave] Found YouTube URL, fetching info before saving...');
          try {
            const songInfo = await youtubeService.getSongInfo(youtubeUrl);
            console.log('[handleNewRowSave] Fetched info:', songInfo);
            // Update the values array directly
            values[songColIndex] = songInfo.song;
            values[artistColIndex] = songInfo.artist;
            console.log('[handleNewRowSave] Updated values array with fetched info.');
          } catch (infoError) {
            console.error('[handleNewRowSave] Error fetching song info for new row:', infoError);
            // Proceed with saving even if info fetch fails
          }
        }
      }
      // --- End of info fetch --- 

      // Get the current state of the sheet to find insert position
      const sheetData = await sheetsService.getData(SPREADSHEET_ID, 'Sheet1');
      if (!sheetData.values) {
        throw new Error('Failed to get sheet data');
      }

      // Find the first blank row after the last data row
      let insertRow = FIRST_DATA_ROW;
      for (let i = 1; i < sheetData.values.length; i++) {
        if (!sheetData.values[i] || sheetData.values[i].every(cell => !cell || cell.trim() === '')) {
          insertRow = i + 1;
          break;
        }
      }
      if (insertRow === FIRST_DATA_ROW && sheetData.values.length > 1) {
        insertRow = sheetData.values.length + 1;
      }
      
      // Insert the potentially updated new row
      const range = `Sheet1!A${insertRow}:${String.fromCharCode(65 + values.length - 1)}${insertRow}`;
      console.log(`[handleNewRowSave] Inserting new row at ${range} with values:`, values);
      await sheetsService.updateData(SPREADSHEET_ID, range, [values]);
      
      // Reload data to reflect the changes (this will include the sort)
      console.log('[handleNewRowSave] New row saved, reloading data...');
      await loadData();
    } catch (err) {
      console.error('[handleNewRowSave] Error saving new row:', err);
      setError('Failed to save new row');
    }
  };

  const handleCellCancel = (rowIndex: number, colIndex: number) => {
    setRows(prevRows => {
      const newRows = [...prevRows];
      newRows[rowIndex].cells[colIndex].isEditing = false;
      return newRows;
    });
  };

  const handleSort = (columnName: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === columnName) {
      direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
    }
    setSortConfig({ key: columnName, direction });
    
    // Sort the rows in the frontend without modifying the Google Sheet
    setRows(prevRows => {
      const [blankRow, ...dataRows] = prevRows;
      const columnIndex = headers.findIndex(h => h.toLowerCase().includes(columnName.toLowerCase()));
      
      const sortedDataRows = [...dataRows].sort((a, b) => {
        const valueA = a.cells[columnIndex]?.value?.toLowerCase() || '';
        const valueB = b.cells[columnIndex]?.value?.toLowerCase() || '';
        return direction === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      });
      
      return [blankRow, ...sortedDataRows];
    });
  };

  const getColumnStyle = (columnType: string): React.CSSProperties => {
    const name = columnType.toLowerCase();
    
    if (name.includes('song') || name.includes('artist')) {
      return {
        whiteSpace: 'nowrap',
        width: 'fit-content',
        minWidth: '150px',
        maxWidth: 'none'
      };
    }
    if (name.includes('zoom')) {
      return {
        width: '60px',
        minWidth: '60px',
        maxWidth: '60px',
        textAlign: 'center',
        padding: '0 8px'
      };
    }
    if (name.includes('s3 url')) {
      return {
        width: '160px',
        minWidth: '160px',
        maxWidth: '160px',
        textAlign: 'left',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis'
      };
    }
    if (name.includes('vertical url')) {
      return {
        width: '160px', // Increased from 120px
        minWidth: '160px', // Added to ensure minimum width
        textAlign: 'left'
      };
    }
    if (name.includes('youtube')) {
      return {
        width: '200px',
        textAlign: 'left'
      };
    }
    if (name.includes('in point')) {
      return {
        width: '8ch',
        minWidth: '8ch',
        maxWidth: '8ch',
        textAlign: 'center'
      };
    }
    if (name.includes('duration')) {
      return {
        width: '9ch',
        minWidth: '9ch',
        maxWidth: '9ch',
        textAlign: 'center'
      };
    }
    if (name.includes('peak')) {
      return {
        width: '5ch',
        minWidth: '5ch',
        maxWidth: '5ch',
        textAlign: 'center',
        padding: '0 8px'
      };
    }
    if (name.includes('weeks on')) {
      return {
        width: '120px',
        minWidth: '120px',
        maxWidth: '120px',
        textAlign: 'center',
        padding: '0 8px'
      };
    }
    return {
      width: 'auto',
      minWidth: '120px',
      textAlign: 'left'
    };
  };

  const handleCellBlur = (rowIndex: number, colIndex: number) => {
    // First, set the cell to not editing
    setRows(prevRows => {
      const newRows = [...prevRows];
      if (newRows[rowIndex] && newRows[rowIndex].cells[colIndex]) { // Add boundary checks
        newRows[rowIndex].cells[colIndex].isEditing = false;
        console.log(`[DEBUG] handleCellBlur: for row ${rowIndex}, col ${colIndex}, isEditing set to false. Header: ${headers[colIndex]}`); // Added log
      } else {
        console.warn(`[DEBUG] handleCellBlur: Could not find cell at row ${rowIndex}, col ${colIndex} to set isEditing to false.`);
      }
      return newRows;
    });

    // For rows other than the first row, save changes to Google Sheets immediately
    if (rowIndex !== 0) {
      console.log(`[DEBUG] handleCellBlur: rowIndex is ${rowIndex}, calling handleCellSave for col ${colIndex}. Header: ${headers[colIndex]}`); // Added log
      handleCellSave(rowIndex, colIndex);
    } else {
      console.log(`[DEBUG] handleCellBlur: rowIndex is 0 (new row), not calling handleCellSave for col ${colIndex}. Header: ${headers[colIndex]}`); // Added log
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, rowIndex: number, colIndex: number) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      
      // Calculate next cell position
      let nextRowIndex = rowIndex;
      let nextColIndex = colIndex + 1;
      
      // Skip the trash can column (index 0)
      if (nextColIndex === 0) {
        nextColIndex = 1; // Start at Song column (index 1)
      }
      
      // If we're at the Peak column (last data column), move to the Song column of the next row
      if (nextColIndex === headers.length) {  // Peak is the last column (headers.length includes trash can)
        nextColIndex = 1; // Start at Song column (index 1)
        nextRowIndex = rowIndex + 1;
      }
      
      // Only proceed if we have a valid next cell
      if (nextRowIndex < rows.length) {
        // First, set the current cell to not editing
        setRows(prevRows => {
          const newRows = [...prevRows];
          newRows[rowIndex].cells[colIndex].isEditing = false;
          return newRows;
        });

        // Then, set the next cell to editing and focus it
        requestAnimationFrame(() => {
          setRows(prevRows => {
            const newRows = [...prevRows];
            newRows[nextRowIndex].cells[nextColIndex].isEditing = true;
            return newRows;
          });

          // Focus the next cell's input
          const nextInput = document.querySelector(`[data-row="${nextRowIndex}"][data-col="${nextColIndex}"] input`);
          if (nextInput) {
            (nextInput as HTMLInputElement).focus();
          }
        });
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (rowIndex === 0) {
        // Cancel all cells in the new row
        setRows(prevRows => {
          const newRows = [...prevRows];
          newRows[rowIndex].cells.forEach(cell => {
            cell.isEditing = false;
            cell.value = '';
          });
          return newRows;
        });
      } else {
        handleCellCancel(rowIndex, colIndex);
      }
    } else if ((e.key === 'Enter' || e.key === 'Return') && e.metaKey) {
      e.preventDefault();
      if (rowIndex === 0) {
        handleNewRowSave(rowIndex);
      } else {
        setRows(prevRows => {
          const newRows = [...prevRows];
          newRows[rowIndex].cells[colIndex].isEditing = false;
          return newRows;
        });
      }
    }
  };

  const getPlaceholderText = (columnType: string, rowIndex: number): string => {
    if (rowIndex === 0) {
      if (columnType.includes('song')) {
        return 'Add new song here';
      }
      if (columnType.includes('artist')) {
        return 'Artist';
      }
      if (columnType.includes('link')) {
        return 'URL';
      }
      if (columnType.includes('in point') || columnType.includes('duration')) {
        return '0';
      }
      if (columnType.includes('peak')) {
        return '0';
      }
      if (columnType.includes('zoom')) {
        return '';
      }
      return '';
    }
    if (columnType.includes('song')) {
      return 'Song';
    } else if (columnType.includes('artist')) {
      return 'Artist';
    } else if (columnType.includes('link')) {
      return 'URL';
    } else if (columnType.includes('zoom')) {
      return '';
    } else if (columnType.includes('in point')) {
      return '0';
    } else if (columnType.includes('video')) {
      return 'FALSE';
    } else if (columnType.includes('duration')) {
      return '0';
    } else if (columnType.includes('peak')) {
      return '0';
    } else {
      return '0';
    }
  };

  const handleSearchIconClick = (rowIndex: number) => {
    const songColIndex = headers.findIndex(h => h.toLowerCase().includes('song'));
    const artistColIndex = headers.findIndex(h => h.toLowerCase().includes('artist'));

    if (songColIndex !== -1 && artistColIndex !== -1) {
      const song = rows[rowIndex]?.cells[songColIndex]?.value || '';
      const artist = rows[rowIndex]?.cells[artistColIndex]?.value || '';
      const query = `${song} ${artist}`.trim();
      setSearchInitialQuery(query);
    } else {
      setSearchInitialQuery('');
    }
    setSearchTargetRowIndex(rowIndex);
    setSearchDialogOpen(true);
  };

  const handleSearchDialogClose = () => {
    setSearchDialogOpen(false);
    setSearchInitialQuery('');
    setSearchTargetRowIndex(null);
  };

  // Function to fetch song/artist info and update the row
  const fetchAndUpdateSongArtist = async (rowIndex: number, youtubeUrl: string) => {
    if (!youtubeUrl || rowIndex === null) return;

    console.log(`[VideoManager] Fetching song/artist for row ${rowIndex}, URL: ${youtubeUrl}`);
    try {
      const songInfo = await youtubeService.getSongInfo(youtubeUrl);
      console.log(`[VideoManager] Fetched info for row ${rowIndex}:`, songInfo);

      const songColIndex = headers.findIndex(h => h.toLowerCase().includes('song'));
      const artistColIndex = headers.findIndex(h => h.toLowerCase().includes('artist'));

      if (songColIndex === -1 || artistColIndex === -1) {
        console.error('[VideoManager] Could not find Song or Artist columns.');
        return;
      }

      // Update local state first for immediate UI feedback
      setRows(prevRows => {
        const newRows = [...prevRows];
        if (newRows[rowIndex]) {
          if (newRows[rowIndex].cells[songColIndex]) {
             newRows[rowIndex].cells[songColIndex].value = songInfo.song;
          }
          if (newRows[rowIndex].cells[artistColIndex]) {
             newRows[rowIndex].cells[artistColIndex].value = songInfo.artist;
          }
          console.log(`[VideoManager] Updated local state for row ${rowIndex} with song/artist.`);
        } else {
            console.error(`[VideoManager] Row index ${rowIndex} not found in state.`);
        }
        return newRows;
      });

      // Persist changes to Google Sheet if it's an existing row
      if (rowIndex !== 0) {
        console.log(`[VideoManager] Saving fetched song/artist for existing row ${rowIndex}...`);
        await handleCellSave(rowIndex, songColIndex); // Save song
        await handleCellSave(rowIndex, artistColIndex); // Save artist
        console.log(`[VideoManager] Saved fetched song/artist for row ${rowIndex}.`);
      }

    } catch (error) {
      console.error(`[VideoManager] Error fetching or updating song/artist info for row ${rowIndex}:`, error);
      // Optional: Update UI to show an error specific to this action
    }
  };

  const handleSearchDialogSelect = async (videoId: string) => {
    console.log('[VideoManager] handleSearchDialogSelect received videoId:', videoId);
    if (searchTargetRowIndex !== null) {
      const youtubeColIndex = headers.findIndex(h => h.toLowerCase().includes('youtube link'));
      if (youtubeColIndex !== -1) {
        const fullUrl = `https://www.youtube.com/watch?v=${videoId}`;
        console.log('[VideoManager] Constructed URL:', fullUrl);
        console.log('[VideoManager] Target row index:', searchTargetRowIndex);
        console.log('[VideoManager] Target column index:', youtubeColIndex);

        // --- Update local state for the URL ---
        setRows(prevRows => {
          console.log('[VideoManager] Updating rows state with URL...');
          const newRows = [...prevRows];
          if (newRows[searchTargetRowIndex] && newRows[searchTargetRowIndex].cells[youtubeColIndex]) {
            newRows[searchTargetRowIndex].cells[youtubeColIndex].value = fullUrl;
            if (searchTargetRowIndex !== 0) {
               newRows[searchTargetRowIndex].cells[youtubeColIndex].isEditing = false;
            }
            console.log('[VideoManager] URL State update applied:', newRows[searchTargetRowIndex].cells[youtubeColIndex]);
          } else {
            console.error('[VideoManager] Error: Target row or cell not found for URL state update.');
          }
          return newRows;
        });

        // --- Persist URL if it's an existing row ---
        if (searchTargetRowIndex !== 0) {
          console.log('[VideoManager] Calling handleCellSave for URL on existing row...');
          await handleCellSave(searchTargetRowIndex, youtubeColIndex);
          console.log('[VideoManager] handleCellSave for URL completed.');
        }

        // --- Fetch and update Song/Artist info ---
        console.log('[VideoManager] Triggering fetchAndUpdateSongArtist...');
        await fetchAndUpdateSongArtist(searchTargetRowIndex, fullUrl);

      } else {
         console.error('[VideoManager] YouTube Link column not found.');
      }
    } else {
       console.error('[VideoManager] searchTargetRowIndex is null.');
    }
    handleSearchDialogClose();
  };

  const handleDeleteClick = (rowIndex: number) => {
    console.log('=== Delete Button Clicked ===');
    console.log('Local row index clicked:', rowIndex);
    console.log('Row data:', rows[rowIndex]);
    console.log('Total rows in local state:', rows.length);
    console.log('Headers:', headers);
    setRowToDelete(rowIndex);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (rowToDelete === null) return;

    try {
      console.log('=== Delete Confirmation Started ===');
      console.log('Local row index to delete:', rowToDelete);
      console.log('Row data to delete:', rows[rowToDelete]);
      
      // Don't allow deleting the blank row (index 0) or the header row (index 1)
      if (rowToDelete <= 1) {
        console.log('Attempted to delete protected row:', rowToDelete);
        setDeleteDialogOpen(false);
        setRowToDelete(null);
        return;
      }

      // Fix: Map local row index to Google Sheet row number correctly
      // Apply the same mapping logic as in handleCellSave
      const sheetRow = rowToDelete === 0 ? FIRST_DATA_ROW : rowToDelete + 1;
      console.log('Calculated Google Sheet row to delete:', sheetRow);
      
      // Delete the row from Google Sheets
      const range = `Sheet1!A${sheetRow}:${String.fromCharCode(65 + headers.length - 1)}${sheetRow}`;
      console.log('Google Sheets range to delete:', range);
      console.log('Headers length:', headers.length);
      console.log('Last column letter:', String.fromCharCode(65 + headers.length - 1));
      
      console.log('Sending delete request to Google Sheets...');
      await sheetsService.deleteRow(SPREADSHEET_ID, range);
      console.log('Delete request completed');
      
      console.log('Reloading data to reflect deletion...');
      await loadData();
      console.log('Data reload completed');
      
      setDeleteDialogOpen(false);
      setRowToDelete(null);
      console.log('=== Delete Operation Completed ===');
    } catch (err) {
      console.error('Error in delete operation:', err);
      setError('Failed to delete row');
    }
  };

  const handleSendJson = async () => {
    if (!previewData) return;
    
    try {
      console.log('Sending JSON to Google Sheets API...');
      await sheetsService.deleteRow(previewData.spreadsheetId, `Sheet1!A${previewData.resource.requests[0].deleteDimension.range.startIndex + 1}`);
      console.log('Delete request completed');
      
      // Reload data to reflect the deletion
      await loadData();
      
      // Close the preview dialog
      setPreviewDialogOpen(false);
    } catch (err) {
      console.error('Error sending JSON:', err);
      setError('Failed to delete row');
    }
  };

  const handleInPointClick = (rowIndex: number, colIndex: number) => {
    // Find the S3 URL column index
    const s3UrlColIndex = headers.findIndex(h => h.toLowerCase().includes('s3 url'));
    if (s3UrlColIndex === -1) {
      console.error('[VideoManager] S3 URL column not found');
      setError('S3 URL column is missing.');
      return;
    }

    const s3Url = rows[rowIndex].cells[s3UrlColIndex].value;
    if (!s3Url) {
      console.error('[VideoManager] No S3 URL found for row:', rowIndex);
      setError('No S3 URL set for this video.');
      return;
    }

    // Find the in point and duration column indices
    const inPointColIndex = headers.findIndex(h => h.toLowerCase().includes('in point'));
    const durationColIndex = headers.findIndex(h => h.toLowerCase().includes('duration'));
    if (inPointColIndex === -1 || durationColIndex === -1) {
      console.error('[VideoManager] In Point or Duration column not found');
      setError('In Point or Duration column is missing.');
      return;
    }

    const inPointValue = rows[rowIndex].cells[inPointColIndex].value || '0';
    const durationValue = rows[rowIndex].cells[durationColIndex].value || '0'; // Get duration value

    console.log(`[VideoManager] handleInPointClick - Row: ${rowIndex}, S3 URL: ${s3Url}, In Point: ${inPointValue}, Duration: ${durationValue}`);

    // Set state to open the player
    setCurrentRowIndex(rowIndex);
    setCurrentVideoUrl(s3Url);
    setCurrentInPoint(inPointValue);
    setCurrentDuration(durationValue); // Set duration state

    setTimeout(() => {
       console.log('[VideoManager] Opening video player from handleInPointClick');
       setVideoPlayerDialogOpen(true);
    }, 100);
  };

  const handleVideoPlayerClose = () => {
    console.log('[VideoManager] Closing video player dialog.');
    setVideoPlayerDialogOpen(false);
    setCurrentVideoUrl('');
    setCurrentRowIndex(-1);
    setCurrentInPoint('0');
    setCurrentDuration('0'); // Reset duration state
    setIsPreviewing(false);
    
    // Instead of relying on URL params which might be cleared, use our state
    if (openedFromCharts) {
      console.log('[VideoManager] Returning to charts page after closing video opened from charts');
      navigate('/');
      // Reset the state
      setOpenedFromCharts(false);
    } else {
      console.log('[VideoManager] Not returning to charts (openedFromCharts=false)');
    }
  };

  const handlePreview = () => {
    if (currentRowIndex === -1 || isPreviewing) return;

    const videoElement = document.getElementById('video-player') as HTMLVideoElement | null;
    if (!videoElement) return;

    // Use local state for preview start point
    const inPoint = parseFloat(currentInPoint);
    let duration = parseFloat(currentDuration); // Use local state for duration

    if (isNaN(inPoint)) {
      console.error('Invalid In Point value for preview.');
      setError('Invalid In Point value for preview.');
      return;
    }
    if (isNaN(duration) || duration <= 0) {
        duration = 5;
        console.warn(`Invalid or zero duration, defaulting to 5s preview.`);
    }

    console.log(`[VideoManager] Previewing from ${inPoint}s for ${duration}s`);
    setIsPreviewing(true);
    videoElement.currentTime = inPoint;
    videoElement.play();

    // Clear existing timeout if any
    if (previewTimeout) clearTimeout(previewTimeout);

    // Set timeout to pause after duration
    const timeout = setTimeout(() => {
      if (videoElement) {
        videoElement.pause();
      }
      setIsPreviewing(false);
      setPreviewTimeout(null);
      console.log('[VideoManager] Preview finished.');
    }, duration * 1000);
    setPreviewTimeout(timeout);
  };

  useEffect(() => {
    const initializeApp = async () => {
      try {
        await sheetsService.init();
        setIsInitialized(true);
        const signedIn = sheetsService.isSignedIn();
        setIsSignedIn(signedIn);
        if (signedIn) {
          await loadData();
        }
      } catch (err) {
        console.error('Error initializing app:', err);
        setError('Error initializing app. Please refresh the page and try again.');
      } finally {
        setLoading(false);
      }
    };

    initializeApp();
    
    // Check if we need to automatically open the player with a specific video
    const params = new URLSearchParams(location.search);
    const hasPlayerParam = params.get('player') === 'true';
    const songParam = params.get('song');
    const artistParam = params.get('artist');
    const s3UrlParam = params.get('s3url');
    
    console.log(`[VideoManager] URL params: player=${hasPlayerParam}, song=${songParam}, artist=${artistParam}, s3url=${s3UrlParam}`);
    
    // Save whether we were opened from charts
    if (hasPlayerParam) {
      console.log('[VideoManager] Setting openedFromCharts=true');
      setOpenedFromCharts(true);
    }
    
    // If s3url is provided directly, use it
    if (hasPlayerParam && s3UrlParam) {
      console.log(`[VideoManager] Opening video player with provided S3 URL: ${s3UrlParam}`);
      setCurrentVideoUrl(s3UrlParam);
      setCurrentInPoint('0'); // Default In Point
      setCurrentDuration('0'); // Default Duration
      setCurrentRowIndex(-1);
      setVideoPlayerDialogOpen(true);
    }
    // Otherwise if we have song/artist, wait for data to load and find the matching row
    else if (hasPlayerParam && songParam && artistParam) {
      // This will be handled by another useEffect after data loads
      console.log(`[VideoManager] Will search for video with song="${songParam}", artist="${artistParam}"`);
    }
  }, []);

  // Add a separate effect to handle finding and opening videos by song/artist after data loads
  useEffect(() => {
    // Only run when data has finished loading and we have the player param with song/artist
    if (!loading && isInitialized && rows.length > 0 && headers.length > 0) {
      const params = new URLSearchParams(location.search);
      const hasPlayerParam = params.get('player') === 'true';
      const songParam = params.get('song');
      const artistParam = params.get('artist');
      const s3UrlParam = params.get('s3url');
      
      // Skip if we don't have the necessary params
      if (!hasPlayerParam) return;
      
      // Immediately clear URL parameters to prevent duplicate processing
      console.log('[VideoManager] Clearing URL parameters immediately to prevent duplicates');
      navigate('/video-manager', { replace: true });
      
      // If S3 URL is directly provided, use it directly without searching for the row
      if (s3UrlParam) {
        console.log(`[VideoManager] Using provided S3 URL directly: ${s3UrlParam}`);
        setCurrentVideoUrl(s3UrlParam);
        setCurrentInPoint('0'); // Default In Point
        setCurrentDuration('0'); // Default Duration
        setCurrentRowIndex(-1);
        
        // If we also have song/artist, try to find the matching row for In Point
        if (songParam && artistParam) {
          console.log(`[VideoManager] Looking for matching row to get In Point, song="${songParam}", artist="${artistParam}"`);
          
          // Find the column indices
          const songColIndex = headers.findIndex(h => h.toLowerCase().includes('song'));
          const artistColIndex = headers.findIndex(h => h.toLowerCase().includes('artist'));
          const s3UrlColIndex = headers.findIndex(h => h.toLowerCase().includes('s3 url'));
          const inPointColIndex = headers.findIndex(h => h.toLowerCase().includes('in point'));
          const seekInColIndex = headers.findIndex(h => h.toLowerCase().includes('seek in'));
          const durationColIndex = headers.findIndex(h => h.toLowerCase().includes('duration'));
          
          if (songColIndex !== -1 && artistColIndex !== -1 && s3UrlColIndex !== -1) {
            // Find matching row
            const matchingRowIndex = rows.findIndex(row => {
              if (!row.cells[songColIndex] || !row.cells[artistColIndex]) return false;
              
              const rowSong = row.cells[songColIndex].value?.toLowerCase().trim() || '';
              const rowArtist = row.cells[artistColIndex].value?.toLowerCase().trim() || '';
              const paramSong = songParam.toLowerCase().trim();
              const paramArtist = artistParam.toLowerCase().trim();
              
              return rowSong === paramSong && rowArtist === paramArtist;
            });
            
            if (matchingRowIndex !== -1) {
              console.log(`[VideoManager] Found matching row at index ${matchingRowIndex}`);
              setHighlightedRow(matchingRowIndex);

              const s3Url = rows[matchingRowIndex].cells[s3UrlColIndex]?.value || '';
              console.log(`[VideoManager] S3 URL found: "${s3Url}"`);

              if (s3Url) {
                console.log('[VideoManager] Setting current row and S3 video URL');
                const inPointValue = inPointColIndex !== -1 ? rows[matchingRowIndex].cells[inPointColIndex]?.value || '0' : '0';
                const seekInValue = seekInColIndex !== -1 ? rows[matchingRowIndex].cells[seekInColIndex]?.value || '0' : '0';
                const durationValue = durationColIndex !== -1 ? rows[matchingRowIndex].cells[durationColIndex]?.value || '0' : '0';

                setTimeout(() => {
                  setCurrentRowIndex(matchingRowIndex);
                  setCurrentVideoUrl(s3Url);
                  setCurrentInPoint(inPointValue);
                  setCurrentSeekIn(seekInValue);
                  setCurrentDuration(durationValue);

                  setTimeout(() => {
                    console.log('[VideoManager] Opening video player dialog NOW with S3 URL:', s3Url);
                    setVideoPlayerDialogOpen(true);
                    setTimeout(() => {
                      console.log('[VideoManager] Clearing URL parameters (found S3 URL)');
                      navigate('/video-manager', { replace: true });
                    }, 200);
                  }, 100);
                }, 100);
              }
            }
          }
        }
        
        // Set state *before* opening dialog
        console.log(`[VideoManager] Data set for direct S3 URL, opening player now.`);
        // Directly open dialog with no delay
        setVideoPlayerDialogOpen(true);

        return; // Important: return early to avoid the row search logic below
      }
      
      // If we don't have S3 URL param but have song/artist, try to find the matching row and S3 URL
      if (songParam && artistParam) {
        console.log(`[VideoManager] Data loaded, searching for song="${songParam}", artist="${artistParam}"`);
        
        // Find the column indices
        const songColIndex = headers.findIndex(h => h.toLowerCase().includes('song'));
        const artistColIndex = headers.findIndex(h => h.toLowerCase().includes('artist'));
        const s3UrlColIndex = headers.findIndex(h => h.toLowerCase().includes('s3 url'));
        const inPointColIndex = headers.findIndex(h => h.toLowerCase().includes('in point'));
        const seekInColIndex = headers.findIndex(h => h.toLowerCase().includes('seek in'));
        const durationColIndex = headers.findIndex(h => h.toLowerCase().includes('duration')); // Add duration index

        if (songColIndex === -1 || artistColIndex === -1 || s3UrlColIndex === -1) {
          console.error(`[VideoManager] Could not find required columns. Indices: song=${songColIndex}, artist=${artistColIndex}, s3=${s3UrlColIndex}`);
          return;
        }
        
        // Find the matching row
        const matchingRowIndex = rows.findIndex(row => {
          if (!row.cells[songColIndex] || !row.cells[artistColIndex]) return false;
          
          const rowSong = row.cells[songColIndex].value?.toLowerCase().trim() || '';
          const rowArtist = row.cells[artistColIndex].value?.toLowerCase().trim() || '';
          const paramSong = songParam.toLowerCase().trim();
          const paramArtist = artistParam.toLowerCase().trim();
          
          return rowSong === paramSong && rowArtist === paramArtist;
        });
        
        if (matchingRowIndex !== -1) {
          // IMPORTANT: Use the S3 URL column, not the YouTube Link column
          const s3Url = rows[matchingRowIndex].cells[s3UrlColIndex].value;
          const inPoint = inPointColIndex !== -1 ? rows[matchingRowIndex].cells[inPointColIndex].value || '0' : '0';
          const seekInValue = seekInColIndex !== -1 ? rows[matchingRowIndex].cells[seekInColIndex]?.value || '0' : '0';
          const durationValue = durationColIndex !== -1 ? rows[matchingRowIndex].cells[durationColIndex].value || '0' : '0'; // Get duration value using durationColIndex

          if (s3Url) {
            console.log(`[VideoManager] Found matching row ${matchingRowIndex} with S3 URL: ${s3Url}`);
            setCurrentVideoUrl(s3Url);
            setCurrentInPoint(inPoint);
            setCurrentSeekIn(seekInValue);
            setCurrentDuration(durationValue); // Set duration using the correct value
            setCurrentRowIndex(matchingRowIndex);
            // Set state and immediately open dialog with no delay
            console.log(`[VideoManager] Data set for song/artist, opening player now.`);
            setVideoPlayerDialogOpen(true);
          } else {
            console.warn(`[VideoManager] Found matching row ${matchingRowIndex} but no S3 URL is available`);
          }
        } else {
          console.warn(`[VideoManager] No matching row found for song="${songParam}", artist="${artistParam}"`);
        }
      }
    }
  }, [loading, isInitialized, rows, headers, navigate, location.search]); // Added location.search to dependency array

  // --- S3 URL Save Handler ---
  const handleS3UrlSave = async (newUrl: string) => {
    if (!editingS3UrlState) return;
    const { row, col } = editingS3UrlState;
    console.log(`[handleS3UrlSave] Saving Row: ${row}, Col: ${col}, New URL: ${newUrl}`);
    await handleCellChange(row, col, newUrl); // Update local state first
    await handleCellSave(row, col); // Persist change
    setEditingS3UrlState(null); // Close dialog
  };

  // --- Vertical URL Save Handler ---
  const handleVerticalUrlSave = async (newUrl: string) => {
    if (!editingVerticalUrlState) return;
    const { row, col } = editingVerticalUrlState;
    console.log(`[handleVerticalUrlSave] Saving Row: ${row}, Col: ${col}, New URL: ${newUrl}`);
    await handleCellChange(row, col, newUrl); // Update local state first
    await handleCellSave(row, col); // Persist change
    setEditingVerticalUrlState(null); // Close dialog
  };

  // --- Handle Vertical Video Upload ---
  const handleVerticalVideoUpload = async () => {
    if (!verticalVideoUploadFile || !editingVerticalUrlState) {
      console.error('[handleVerticalVideoUpload] No file or editing state available');
      return;
    }

    const { row, song, artist } = editingVerticalUrlState;
    if (!song || !artist) {
      console.error('[handleVerticalVideoUpload] Missing song or artist information');
      setError('Missing song or artist information for the file name');
      return;
    }

    // Format the filename: "(Artist) - (Song) VERTICAL.mp4"
    const fileName = `${artist} - ${song} VERTICAL.mp4`;
    console.log(`[handleVerticalVideoUpload] Uploading as: ${fileName}`);

    try {
      // Create FormData to send the file to the server
      const formData = new FormData();
      formData.append('file', verticalVideoUploadFile);
      formData.append('destination', 'Billboard/Videos');
      formData.append('filename', fileName);

      // Upload the file with progress tracking using XMLHttpRequest
      const uploadResult = await new Promise<any>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        xhr.open('POST', '/api/upload-s3-video', true);
        
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            setVerticalUploadProgress(percentComplete);
          }
        };
        
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText);
              resolve(response);
            } catch (e) {
              reject(new Error('Invalid response format'));
            }
          } else {
            reject(new Error(`Upload failed with status: ${xhr.status}`));
          }
        };
        
        xhr.onerror = () => {
          reject(new Error('Network error during upload'));
        };
        
        xhr.send(formData);
      });

      console.log('[handleVerticalVideoUpload] Upload success:', uploadResult);
      
      // Update the vertical URL cell with the new S3 URL
      const s3Url = `https://c1-scott-scratchdisk.s3.amazonaws.com/Billboard/Videos/${encodeURIComponent(fileName)}`;
      await handleVerticalUrlSave(s3Url);
      
      // Reset upload state
      setVerticalVideoUploadFile(null);
      setVerticalUploadProgress(0);
    } catch (error) {
      console.error('[handleVerticalVideoUpload] Error:', error);
      setError('Failed to upload vertical video: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  // --- Handle S3 Video Upload ---
  const handleS3VideoUpload = async () => {
    // This function will now primarily set up the confirmation dialog
    if (!s3VideoUploadFile || !editingS3UrlState) {
      console.error('[handleS3VideoUpload] No file selected or editingS3UrlState is not set.');
      setError('No file selected or context is missing for upload.');
      return;
    }

    const { row, song, artist } = editingS3UrlState;
    if (!song || !artist) {
      console.error('[handleS3VideoUpload] Missing song or artist information for filename.');
      setError('Song and Artist information are required to suggest a filename.');
      return;
    }

    const s3UrlColIndex = headers.findIndex(h => h.toLowerCase().includes('s3 url'));
    if (s3UrlColIndex === -1) {
      console.error('[handleS3VideoUpload] S3 URL column not found in headers.');
      setError('Configuration error: S3 URL column not found.');
      return;
    }

    const initialFileName = `${artist} - ${song}.mp4`;
    console.log(`[handleS3VideoUpload] Opening confirmation for: ${initialFileName}`);

    setCurrentUploadFileName(initialFileName);
    setPendingUploadFile(s3VideoUploadFile); // Keep the originally selected file
    setUploadTargetRowInfo({ rowIndex: row, s3Key: initialFileName });
    setIsFileNameConfirmationDialogOpen(true);
    
    // Clear s3VideoUploadFile from state as it's now in pendingUploadFile, 
    // this helps the UrlDialog reset its file input if the user cancels confirmation
    setS3VideoUploadFile(null);
  };

  // Actual S3 Upload logic (called after filename confirmation)
  const executeS3Upload = async (confirmedFileName: string) => {
    if (!pendingUploadFile || !uploadTargetRowInfo) {
      setError("No file or target row info for upload.");
      return;
    }
    setError(null);
    setS3UploadProgress(0);
    setIsUploadingS3(true); // Indicate upload has started

    const formData = new FormData();
    formData.append('video', pendingUploadFile);
    formData.append('fileName', confirmedFileName);

    try {
      const uploadResult = await new Promise<any>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', '/api/upload-s3-video', true);
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            setS3UploadProgress(percentComplete);
          }
        };
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText);
              resolve(response);
            } catch (e) {
              reject(new Error('Invalid response format'));
            }
          } else {
            // Attempt to parse error response from server
            let errorMsg = `Upload failed with status: ${xhr.status}`;
            try {
                const errorResponse = JSON.parse(xhr.responseText);
                if (errorResponse && errorResponse.message) {
                    errorMsg += ` - ${errorResponse.message}`;
                }
            } catch (parseError) {
                // Ignore if response is not JSON or unparsable
            }
            reject(new Error(errorMsg));
          }
        };
        xhr.onerror = () => {
          reject(new Error('Network error during upload'));
        };
        xhr.send(formData);
      });

      console.log('[executeS3Upload] Upload success:', uploadResult);
      
      // Construct S3 URL based on the *confirmed* and potentially *edited* filename
      const s3Url = `https://c1-scott-scratchdisk.s3.amazonaws.com/Billboard/Videos/${encodeURIComponent(confirmedFileName)}`;
      
      await handleCellChange(uploadTargetRowInfo.rowIndex, headers.findIndex(h => h.toLowerCase().includes('s3 url')), s3Url);
      await handleCellSave(uploadTargetRowInfo.rowIndex, headers.findIndex(h => h.toLowerCase().includes('s3 url')), s3Url);
      
      setPendingUploadFile(null);
      setCurrentUploadFileName('');
      setS3UploadProgress(0);
      setIsFileNameConfirmationDialogOpen(false);
      setUploadTargetRowInfo(null);
      setEditingS3UrlState(null); // Close the UrlDialog as well

    } catch (error) {
      console.error('[executeS3Upload] Error:', error);
      setError(`Failed to upload S3 video: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setS3UploadProgress(0); // Reset progress on error
      // Don't close the dialog on error, let user try again or cancel
    } finally {
      setIsUploadingS3(false); // Indicate upload has finished
      setS3UploadProgress(0); // Reset progress
    }
  };

  // Function to render the filename confirmation dialog
  const renderFileNameConfirmationDialog = () => {
    if (!uploadTargetRowInfo) return null; // Don't render if no target
  
    return (
      <Dialog
        open={isFileNameConfirmationDialogOpen}
        onClose={() => {
          setIsFileNameConfirmationDialogOpen(false);
          setPendingUploadFile(null);
          setCurrentUploadFileName('');
          setUploadTargetRowInfo(null);
          setS3UploadProgress(0); // Reset progress if dialog is cancelled
          setError(null); // Clear error on cancel
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm File Name for S3 Upload</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Please confirm or edit the filename for the S3 upload.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="File Name"
            type="text"
            fullWidth
            variant="outlined"
            value={currentUploadFileName}
            onChange={(e) => setCurrentUploadFileName(e.target.value)}
            helperText="The video will be uploaded to Billboard/Videos/ on S3 with this name."
          />
          {s3UploadProgress > 0 && s3UploadProgress < 100 && (
            <Box sx={{ width: '100%', mt: 2 }}>
              <LinearProgress variant="determinate" value={s3UploadProgress} />
              <Typography variant="body2" sx={{ mt: 0.5, textAlign: 'center' }}>
                Uploading: {s3UploadProgress}%
              </Typography>
            </Box>
          )}
          {error && !(s3UploadProgress > 0 && s3UploadProgress < 100) && ( // Show error only if not actively uploading
              <Alert severity="error" sx={{mt: 2}}>{error}</Alert>
          )}
          <DialogContentText sx={{ mt: 1, mb: 2 }}>
            Current S3 Key (if any): {uploadTargetRowInfo?.s3Key || 'N/A'}
          </DialogContentText>
          {isUploadingS3 && (
            <Box sx={{ width: '100%', mt: 2, mb:1 }}>
              <LinearProgress variant="determinate" value={s3UploadProgress} />
              <Typography variant="caption" display="block" gutterBottom align="center">
                {s3UploadProgress > 0 ? `Uploading: ${s3UploadProgress}%` : 'Preparing upload...'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ pb: 2, px:3 }}>
          <Button onClick={() => {
            setIsFileNameConfirmationDialogOpen(false);
            setPendingUploadFile(null);
            setCurrentUploadFileName('');
            setUploadTargetRowInfo(null);
            setS3UploadProgress(0);
            setError(null);
            setIsUploadingS3(false); // Ensure this is reset on cancel
          }} color="secondary" disabled={isUploadingS3}>
            Cancel
          </Button>
          <Button 
            onClick={() => executeS3Upload(currentUploadFileName)} 
            color="primary" 
            variant="contained"
            disabled={isUploadingS3 || !currentUploadFileName.trim()} // Disable if uploading or filename is empty
          >
            {isUploadingS3 ? 'Uploading...' : 'Confirm & Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderCell = (cell: Cell, rowIndex: number, colIndex: number) => {
    const columnType = headers[colIndex]?.toLowerCase() || '';
    const isEmpty = !cell.value;

    // --- Logic for "Add New" Row (rowIndex === 0) ---
    if (rowIndex === 0) {
      if (cell.isEditing) {
        // Editing state for row 0
        if (columnType.includes('zoom')) {
          return (
            <Checkbox 
              checked={cell.value === 'TRUE'} 
              onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.checked ? 'TRUE' : 'FALSE')}
              onBlur={() => handleCellBlur(rowIndex, colIndex)}
              sx={{ 
                '& .MuiSvgIcon-root': {
                  fontSize: '1.25rem',
                  color: 'rgba(0, 0, 0, 0.54)'
                }
              }}
            />
          );
        } else if (columnType.includes('youtube link')) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <TextField
                fullWidth
                value={cell.value || ''}
                onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.value)}
                onBlur={() => handleCellBlur(rowIndex, colIndex)}
                onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
                placeholder={getPlaceholderText(columnType, rowIndex)}
                autoFocus
                size="small"
                sx={{ '& .MuiInputBase-root': { height: '32px' }, flexGrow: 1 }}
                inputProps={{ 'data-row': rowIndex, 'data-col': colIndex }}
              />
              <IconButton
                size="small"
                onClick={() => handleSearchIconClick(rowIndex)}
                title="Search YouTube"
                sx={{ flexShrink: 0 }}
              >
                <SearchIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        } else {
          return (
            <TextField
              fullWidth
              value={cell.value || ''}
              onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.value)}
              onBlur={() => handleCellBlur(rowIndex, colIndex)}
              placeholder={getPlaceholderText(columnType, rowIndex)}
              autoFocus
              size="small"
              sx={{ 
                '& .MuiInputBase-root': {
                  height: '32px'
                },
                '& .MuiInputBase-input': {
                  textAlign: columnType.includes('in point') || columnType.includes('duration') || columnType.includes('peak') || columnType.includes('zoom') ? 'center' : 'left'
                }
              }}
            />
          );
        }
      } else {
        // Non-editing state for row 0
        if (columnType.includes('zoom')) {
          return (
            <Box
              onClick={(e) => {
                console.log('Cell clicked:', { rowIndex, colIndex, columnType });
                e.stopPropagation();
                handleCellEdit(rowIndex, colIndex);
              }}
              sx={{ 
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
            >
              <Checkbox 
                checked={cell.value === 'TRUE'} 
                onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.checked ? 'TRUE' : 'FALSE')}
                sx={{ 
                  '& .MuiSvgIcon-root': {
                    fontSize: '1.25rem',
                    color: 'rgba(0, 0, 0, 0.54)'
                  }
                }}
              />
            </Box>
          );
        } else if (columnType.includes('youtube link')) {
          // Apply truncation logic here as well
          const urlValue = cell.value || '';
          const truncatedUrl = urlValue.length > 20 ? `${urlValue.substring(0, 20)}...` : urlValue;
          const displayText = truncatedUrl || getPlaceholderText(columnType, rowIndex);
          const isEmpty = !urlValue; // isEmpty based on original value
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', cursor: 'pointer' }}
                 onClick={() => handleCellEdit(rowIndex, colIndex)}>
              <Typography
                sx={{ 
                    color: isEmpty ? 'rgba(0, 0, 0, 0.38)' : 'inherit',
                    flexGrow: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap', 
                 }}
              >
                {/* Display the truncated value or placeholder */}
                {displayText}
              </Typography>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); handleSearchIconClick(rowIndex); }}
                title="Search YouTube"
                sx={{ flexShrink: 0 }}
              >
                <SearchIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        } else {
          return (
            <Box
              onClick={(e) => {
                console.log('Cell clicked:', { rowIndex, colIndex, columnType });
                e.stopPropagation();
                handleCellEdit(rowIndex, colIndex);
              }}
              sx={{ 
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: columnType.includes('in point') || columnType.includes('duration') || columnType.includes('peak') || columnType.includes('zoom') ? 'center' : 'flex-start',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  borderRadius: '4px'
                }
              }}
            >
              <Typography 
                sx={{ 
                  color: isEmpty ? 'rgba(0, 0, 0, 0.38)' : 'inherit',
                  width: '100%',
                  textAlign: columnType.includes('in point') || columnType.includes('duration') || columnType.includes('peak') || columnType.includes('zoom') ? 'center' : 'left',
                  display: 'block'
                }}
              >
                {cell.value || getPlaceholderText(columnType, rowIndex)}
              </Typography>
            </Box>
          );
        }
      }
    }

    // --- Logic for Other Rows (rowIndex > 0) ---
    else {
      // Handle specific columns first
      if (columnType.includes('in point') || columnType.includes('duration')) {
        return (
          <Box
            onClick={() => handleInPointClick(rowIndex, colIndex)}
            sx={{ 
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <Typography 
              sx={{ 
                color: isEmpty ? 'rgba(0, 0, 0, 0.38)' : 'inherit',
                textAlign: 'center',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              {cell.value || getPlaceholderText(columnType, rowIndex)}
            </Typography>
          </Box>
        );
      } else if (columnType.includes('s3 url')) {
        // Mirror the Vertical URL functionality for S3 URL
        if (cell.value) {
          const displayText = cell.value.length > 20 ? `${cell.value.substring(0, 20)}...` : cell.value;
          return (
            <Box
              sx={{ 
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1
              }}
            >
              <Link
                component="button"
                onClick={(e) => {
                  e.stopPropagation();
                  // Get song and artist for potential upload
                  const songColIndex = headers.findIndex(h => h.toLowerCase().includes('song'));
                  const artistColIndex = headers.findIndex(h => h.toLowerCase().includes('artist'));
                  const song = songColIndex !== -1 ? rows[rowIndex].cells[songColIndex].value : '';
                  const artist = artistColIndex !== -1 ? rows[rowIndex].cells[artistColIndex].value : '';
                  
                  // Also get the current YouTube URL for this row
                  const ytLinkColIndex = headers.findIndex(h => h.toLowerCase().includes('youtube link'));
                  const currentYoutubeUrl = ytLinkColIndex !== -1 ? rows[rowIndex].cells[ytLinkColIndex]?.value : '';
                  
                  setEditingS3UrlState({ 
                    row: rowIndex, 
                    col: colIndex, 
                    url: cell.value || '',
                    song,
                    artist,
                    currentYoutubeUrl: currentYoutubeUrl || '' // Ensure it's at least an empty string
                  });
                }}
                title={cell.value}
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flexGrow: 1,
                  textAlign: 'left',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                {displayText}
              </Link>
            </Box>
          );
        } else {
          // If S3 URL is empty, show just an upload icon like Vertical URL
          return (
            <Box
              sx={{ 
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1
              }}
            >
              <IconButton // Changed from Button
                size="small" // IconButton has its own size prop
                onClick={async (e) => { 
                  e.stopPropagation();
                  
                    const songColIndex = headers.findIndex(h => h.toLowerCase().includes('song'));
                    const artistColIndex = headers.findIndex(h => h.toLowerCase().includes('artist'));
                  const song = songColIndex !== -1 ? rows[rowIndex].cells[songColIndex].value : '';
                  const artist = artistColIndex !== -1 ? rows[rowIndex].cells[artistColIndex].value : '';
                  
                  let currentYoutubeUrl = '';
                  const ytLinkColIndex = headers.findIndex(h => h.toLowerCase().includes('youtube link'));
                  if (ytLinkColIndex !== -1 && rows[rowIndex] && rows[rowIndex].cells[ytLinkColIndex]) {
                    currentYoutubeUrl = rows[rowIndex].cells[ytLinkColIndex].value || '';
                  } else {
                    console.warn('YouTube Link column not found when opening S3 dialog for empty S3 URL.');
                  }
                    
                    setEditingS3UrlState({ 
                      row: rowIndex, 
                      col: colIndex, 
                    url: '', // S3 URL is empty in this case
                    song,
                    artist,
                    currentYoutubeUrl // Pass the fetched YouTube URL
                  });
                }}
                // sx removed, IconButton has sensible defaults
              >
                <CloudUploadIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        }
      } else if (columnType.includes('youtube link')) {
        const displayText = cell.value && cell.value.length > 20 ? `${cell.value.substring(0, 20)}...` : cell.value;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            {cell.isEditing ? (
              <TextField
                fullWidth
                value={cell.value || ''}
                onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.value)}
                onBlur={() => handleCellBlur(rowIndex, colIndex)}
                onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
                placeholder={getPlaceholderText(columnType, rowIndex)}
                size="small"
                sx={{ '& .MuiInputBase-root': { height: '32px' }, flexGrow: 1 }}
                inputProps={{ 'data-row': rowIndex, 'data-col': colIndex }}
              />
            ) : (
              <Typography
                onClick={() => handleCellEdit(rowIndex, colIndex)}
                sx={{
                  color: isEmpty ? 'rgba(0, 0, 0, 0.38)' : 'inherit',
                  cursor: 'pointer',
                  flexGrow: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {displayText || getPlaceholderText(columnType, rowIndex)}
              </Typography>
            )}
            <IconButton
              size="small"
              onClick={() => handleSearchIconClick(rowIndex)}
              title="Search YouTube"
              sx={{ flexShrink: 0 }}
            >
              <SearchIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      } else if (columnType.includes('vertical url')) {
        // If Vertical URL exists, show as clickable link
        if (cell.value) {
          const displayText = cell.value.length > 20 ? `${cell.value.substring(0, 20)}...` : cell.value;
          return (
            <Box
              sx={{ 
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1
              }}
            >
              <Link
                component="button"
                onClick={(e) => {
                  e.stopPropagation();
                  // Get song and artist for potential upload
                  const songColIndex = headers.findIndex(h => h.toLowerCase().includes('song'));
                  const artistColIndex = headers.findIndex(h => h.toLowerCase().includes('artist'));
                  const song = songColIndex !== -1 ? rows[rowIndex].cells[songColIndex].value : '';
                  const artist = artistColIndex !== -1 ? rows[rowIndex].cells[artistColIndex].value : '';
                  
                  setEditingVerticalUrlState({ 
                    row: rowIndex, 
                    col: colIndex, 
                    url: cell.value || '',
                    song,
                    artist
                  });
                }}
                title={cell.value}
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flexGrow: 1,
                  textAlign: 'left',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                {displayText}
              </Link>
            </Box>
          );
        } else {
          // If Vertical URL is empty, show Add button
          return (
            <Box
              sx={{ 
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1
              }}
            >
              <Button
                variant="outlined"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  // Get song and artist for potential upload
                  const songColIndex = headers.findIndex(h => h.toLowerCase().includes('song'));
                  const artistColIndex = headers.findIndex(h => h.toLowerCase().includes('artist'));
                  const song = songColIndex !== -1 ? rows[rowIndex].cells[songColIndex].value : '';
                  const artist = artistColIndex !== -1 ? rows[rowIndex].cells[artistColIndex].value : '';
                  
                  setEditingVerticalUrlState({ 
                    row: rowIndex, 
                    col: colIndex, 
                    url: '',
                    song,
                    artist
                  });
                }}
                sx={{ 
                  minWidth: 'unset',
                  padding: '4px'
                }}
              >
                <CloudUploadIcon fontSize="small" />
              </Button>
            </Box>
          );
        }
      } else if (cell.isEditing) {
        if (columnType.includes('zoom') || columnType.includes('video')) {
          console.log(`[DEBUG] renderCell: Row ${rowIndex}, Col ${colIndex} (${columnType}), IS EDITING. Value: ${cell.value}`); // Added log
          return (
            <Checkbox 
              checked={cell.value === 'TRUE'} 
              onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.checked ? 'TRUE' : 'FALSE')}
              onBlur={() => handleCellBlur(rowIndex, colIndex)}
              sx={{ 
                '& .MuiSvgIcon-root': {
                  fontSize: '1.25rem',
                  color: 'rgba(0, 0, 0, 0.54)'
                }
              }}
            />
          );
        } else if (columnType.includes('artist')) {
          const artistOptions = rows
            .slice(1) // Skip the new row
            .map(row => row.cells[colIndex].value)
            .filter((value, index, self) => value && self.indexOf(value) === index); // Get unique values
          
          return (
            <Autocomplete
              freeSolo
              options={artistOptions}
              value={cell.value || ''}
              onChange={(_, newValue) => handleCellChange(rowIndex, colIndex, newValue || '')}
              onInputChange={(_, newInputValue) => handleCellChange(rowIndex, colIndex, newInputValue)}
              onBlur={() => handleCellBlur(rowIndex, colIndex)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  value={cell.value || ''}
                  onBlur={() => handleCellBlur(rowIndex, colIndex)}
                  onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
                  placeholder={getPlaceholderText(columnType, rowIndex)}
                  autoFocus={rowIndex === 0}
                  size="small"
                  sx={{ 
                    '& .MuiInputBase-root': {
                      height: '32px'
                    }
                  }}
                  inputProps={{
                    ...params.inputProps,
                    'data-row': rowIndex,
                    'data-col': colIndex
                  }}
                />
              )}
            />
          );
        } else {
          return (
            <TextField
              fullWidth
              value={cell.value || ''}
              onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.value)}
              onBlur={() => handleCellBlur(rowIndex, colIndex)}
              onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
              placeholder={getPlaceholderText(columnType, rowIndex)}
              autoFocus={rowIndex === 0}
              size="small"
              sx={{ 
                '& .MuiInputBase-root': {
                  height: '32px'
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'rgba(0, 0, 0, 0.38)',
                  opacity: 1
                }
              }}
              inputProps={{
                'data-row': rowIndex,
                'data-col': colIndex
              }}
            />
          );
        }
      }
      // Handle non-editing display for remaining columns
      else {
        if (columnType.includes('video') || columnType.includes('zoom')) {
          console.log(`[DEBUG] renderCell: Row ${rowIndex}, Col ${colIndex} (${columnType}), NOT editing. Value: ${cell.value}`); // Added log
          return (
            <Box
              onClick={() => handleCellEdit(rowIndex, colIndex)}
              sx={{ /* ... styles ... */ }}
            >
              <Checkbox
                checked={cell.value === 'TRUE'}
                onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.checked ? 'TRUE' : 'FALSE')}
                sx={{ /* ... styles ... */ }}
              />
            </Box>
          );
        } else {
          // Default display for text
          const displayText = cell.value || getPlaceholderText(columnType, rowIndex);
          return (
            <Box
              onClick={() => handleCellEdit(rowIndex, colIndex)}
              sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <Typography
                 sx={{
                   color: isEmpty ? 'rgba(0, 0, 0, 0.38)' : 'inherit',
                   width: '100%',
                   textAlign: columnType.includes('in point') || columnType.includes('duration') || columnType.includes('peak') || columnType.includes('weeks on') || columnType.includes('zoom') ? 'center' : 'flex-start',
                   display: 'block'
                 }}
              >
                {displayText}
              </Typography>
            </Box>
          );
        }
      }
    }
  };

  const renderRow = (row: Row, rowIndex: number) => {
    const isHighlighted = rowIndex === highlightedRow;
    
    // Debug log for the first data row (index 1) to check the cell count
    if (rowIndex === 1) {
      console.log(`[DEBUG] Row 1 has ${row.cells.length} cells and headers has ${headers.length} items`);
      row.cells.forEach((cell, idx) => console.log(`[DEBUG] Row 1 Cell ${idx}: "${cell.value || 'empty'}" for header "${headers[idx] || 'unknown'}"`));
    }
    
    return (
      <TableRow 
        key={rowIndex}
        ref={isHighlighted ? highlightedRowRef : null}
        sx={{
          backgroundColor: row.isNew 
            ? 'transparent' 
            : (isHighlighted 
                ? 'rgba(255, 255, 0, 0.15)' 
                : (rowIndex % 2 === 0 ? 'rgba(0, 0, 0, 0.04)' : 'transparent')),
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)'
          },
          transition: 'background-color 0.3s ease',
        }}
      >
        <TableCell sx={{ width: '48px', padding: '0 !important' }}>
          {row.isNew ? (
            row.cells.some(cell => cell.value && cell.value.trim() !== '') ? (
              <Box sx={{ display: 'flex', gap: 1, padding: '0 8px' }}>
                <IconButton
                  size="small"
                  onClick={() => handleNewRowSave(rowIndex)}
                  sx={{ color: 'primary.main' }}
                >
                  ✓
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => row.cells.forEach((_, colIndex) => handleCellCancel(rowIndex, colIndex))}
                  sx={{ color: 'error.main' }}
                >
                  ✕
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ width: '48px' }} /> // Empty box to maintain spacing
            )
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <IconButton
                size="small"
                onClick={() => handleDeleteClick(rowIndex)}
                sx={{ 
                  color: 'error.main',
                  '&:hover': {
                    backgroundColor: 'rgba(211, 47, 47, 0.08)'
                  }
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </TableCell>
        {row.cells.map((cell, colIndex) => {
          const isS3UrlColumn = headers[colIndex]?.toLowerCase().includes('s3 url');
          return (
            <TableCell 
              key={colIndex}
              align={headers[colIndex].toLowerCase().includes('weeks on') ? 'center' : 'left'}
              sx={{
                fontFamily: "'Open Sans', sans-serif",
                ...(isS3UrlColumn && {
                  width: '160px',
                  minWidth: '160px',
                  maxWidth: '160px',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis'
                }),
                ...getColumnStyle(headers[colIndex])
              }}
            >
              {renderCell(cell, rowIndex, colIndex)}
            </TableCell>
          );
        })}
      </TableRow>
    );
  }

  if (!isInitialized) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isSignedIn) {
    return (
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 800 }}>
          Video Manager
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ fontFamily: "'Open Sans', sans-serif" }}>
          Please sign in to access the Video Manager
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
            {error}
          </Alert>
        )}
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSignIn}
          sx={{ mt: 2 }}
        >
          Sign in with Google
        </Button>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography variant="h4" gutterBottom sx={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 800 }}>Video Manager</Typography>
        <Typography color="error" sx={{ fontFamily: "'Open Sans', sans-serif" }}>{error}</Typography>
      </Box>
    );
  }

      return (
        <Box sx={{ 
      mt: 0,
      mx: '2.5%',
      width: '95vw',
      maxWidth: '95vw',
      boxSizing: 'border-box',
          overflow: 'hidden',
      position: 'relative',
      left: '50%',
      transform: 'translateX(-50%)',
      padding: '0 10%'
    }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" gutterBottom sx={{ 
          fontFamily: "'Open Sans', sans-serif", 
          fontWeight: 800, 
          mb: 0,
          mt: 2,
          padding: '8px 0'
        }}>Video Manager</Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            onClick={() => navigate('/upload-video')}
            disabled={!isSignedIn}
            size="small"
            sx={{
              height: '40px',
              padding: '4px 16px'
            }}
          >
            Upload Video
          </Button>
        <Button
          variant="contained"
          onClick={() => navigate('/')} // Navigate to root path
          disabled={!isSignedIn}
          size="small"
          sx={{
            height: '40px',
            padding: '4px 16px'
          }}
        >
          Charts
        </Button>
        </Box>
      </Box>
      <TableContainer 
        component={Paper}
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflowX: 'auto',
          '& .MuiTable-root': {
            width: '100%',
            tableLayout: 'fixed',
            display: 'table'
          },
          '& .MuiTableBody-root': {
            display: 'table-row-group'
          },
          '& .MuiTableRow-root': {
            display: 'table-row'
          },
          '& .MuiTableCell-root': {
            display: 'table-cell',
            fontSize: '0.875rem',
            verticalAlign: 'middle',
            fontFamily: "'Open Sans', sans-serif",
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            borderRight: '1px solid rgba(224, 224, 224, 1)',
            padding: '1px 16px',
            '&:last-child': {
              borderRight: 'none'
            }
          }
        }}
      >
        <Table>
          <TableHead sx={{ 
            bgcolor: 'black',
            '& .MuiTableRow-root': {
              height: '48px'
            },
            '& .MuiTableCell-root': {
              fontWeight: '900 !important',
              fontSize: '1.25rem !important',
              fontFamily: "'Open Sans', sans-serif !important",
              height: '48px !important',
              lineHeight: '1.5 !important',
              color: 'white !important',
              whiteSpace: 'normal !important',
              wordBreak: 'break-word !important',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }
          }}>
            <TableRow>
              <TableCell sx={{ width: '48px', padding: '0 !important' }}></TableCell>
              {headers.map((header, index) => {
                console.log(`[DEBUG] Rendering header ${index}: "${header}"`);
                return (
                <TableCell 
                    key={index} 
                  onClick={() => {
                    if (header.toLowerCase().includes('song') || 
                        header.toLowerCase().includes('artist')) {
                      handleSort(header);
                    }
                  }}
                  sx={{
                    fontWeight: 900,
                    whiteSpace: 'nowrap',
                    color: 'white',
                    bgcolor: 'black',
                    height: '48px',
                    lineHeight: '1.5',
                    fontSize: '1.25rem',
                    fontFamily: "'Open Sans', sans-serif",
                    ...(header.toLowerCase().includes('s3 url') && {
                      width: '160px',
                      minWidth: '160px',
                      maxWidth: '160px'
                    }),
                    ...getColumnStyle(header)
                    }}
                  >
                    {header}
                  {sortConfig.key === header && (
                    <span style={{ marginLeft: '8px' }}>
                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </TableCell>
              )})}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIndex) => renderRow(row, rowIndex))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Song</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this song? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog 
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>API Request Preview</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" gutterBottom>
            This is what would be sent to the Google Sheets API:
          </Typography>
          <pre style={{ 
            backgroundColor: '#f5f5f5', 
            padding: '16px', 
            borderRadius: '4px',
            overflow: 'auto',
            maxHeight: '400px'
          }}>
            {JSON.stringify(previewData, null, 2)}
          </pre>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
          <Button 
            onClick={handleSendJson} 
            variant="contained" 
            color="primary"
          >
            Send JSON
          </Button>
        </DialogActions>
      </Dialog>

      {/* Video Player Dialog */}
      <Dialog
        open={videoPlayerDialogOpen}
        onClose={handleVideoPlayerClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: { xs: '90vw', sm: '85vw', md: '700px' },
            margin: '10px',
            width: '100%',
            overflowX: 'hidden'
          }
        }}
      >
        <DialogTitle>
          Video Player
        </DialogTitle>
        <DialogContent sx={{ 
          overflowX: 'hidden', 
          padding: '16px' 
        }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, pt: 2 }}>
            <TextField
              label="In Point"
              value={currentInPoint} // Use local state
              onChange={handleInPointChange}
              size="small"
              sx={{ 
                width: '100px',
                '& .MuiInputLabel-root': {
                  backgroundColor: 'white',
                  padding: '0 4px',
                  
                }
              }}
            />
            {/* <TextField
              label="Seek In"
              value={currentSeekIn} // Use local state
              onChange={handleSeekInChange}
              size="small"
              sx={{ 
                width: '100px',
                '& .MuiInputLabel-root': {
                  backgroundColor: 'white',
                  padding: '0 4px',
                  
                }
              }}
            /> */}
            <TextField
              label="Duration"
              value={currentDuration} // Use local state for duration
              onChange={handleDurationChange}
              size="small"
              sx={{ 
                width: '100px',
                '& .MuiInputLabel-root': {
                  backgroundColor: 'white',
                  padding: '0 4px',
                  transform: 'translate(14px, -8px) scale(0.75)',
                  '&.Mui-focused': {
                    color: 'primary.main'
                  }
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.87)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                }
              }}
            />
          </Box>
          <Box sx={{ 
            width: '100%', 
            maxHeight: '70vh', 
            display: 'flex', 
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#000',
            borderRadius: 1,
            px: { xs: 1, sm: 2, md: 3 }, // Reduce padding further
            py: 2,
            overflow: 'hidden',
            boxSizing: 'border-box'
          }}>
            {!currentVideoUrl ? (
              <Typography color="white" sx={{ p: 2 }}>
                No video URL provided
              </Typography>
            ) : (
              // Standard video player for all URLs
            <video
              id="video-player"
              src={currentVideoUrl}
              controls
              onTimeUpdate={handleMarkInPoint}
              onLoadedMetadata={(e) => {
                  console.log(`[VideoPlayer] Video loaded with URL: ${currentVideoUrl}`);
                const video = e.target as HTMLVideoElement;
                  const inPoint = parseFloat(currentInPoint);
                if (!isNaN(inPoint)) {
                  video.currentTime = inPoint;
                }
              }}
                onError={(e) => {
                  console.error(`[VideoPlayer] Error loading video:`, e);
              }}
              style={{
                width: '100%',
                maxWidth: '100%',
                maxHeight: '60vh',
                objectFit: 'contain'
              }}
            >
              Your browser does not support the video tag.
            </video>
            )}
          </Box>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={handlePreview}
              disabled={isPreviewing || !currentVideoUrl}
            >
              {isPreviewing ? 'Previewing...' : 'Preview'}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleVideoPlayerClose}>Cancel</Button>
          <Button
            onClick={async () => { // Make save async
              if (currentRowIndex !== -1) {
                console.log(`[VideoPlayer Save] Saving In Point: ${currentInPoint}, Seek In: ${currentSeekIn}, Duration: ${currentDuration} for row index: ${currentRowIndex}`);
                // Update local rows state immediately for visual consistency (optional but good)
                const inPointColIndex = headers.findIndex(h => h.toLowerCase().includes('in point'));
                const seekInColIndex = headers.findIndex(h => h.toLowerCase().includes('seek in'));
                const durationColIndex = headers.findIndex(h => h.toLowerCase().includes('duration'));

                setRows(prevRows => {
                   const newRows = [...prevRows];
                   if (newRows[currentRowIndex]) {
                      if (inPointColIndex !== -1 && newRows[currentRowIndex].cells[inPointColIndex]) {
                         newRows[currentRowIndex].cells[inPointColIndex].value = currentInPoint;
                      }
                      if (seekInColIndex !== -1 && newRows[currentRowIndex].cells[seekInColIndex]) {
                         newRows[currentRowIndex].cells[seekInColIndex].value = currentSeekIn;
                      }
                      if (durationColIndex !== -1 && newRows[currentRowIndex].cells[durationColIndex]) {
                         newRows[currentRowIndex].cells[durationColIndex].value = currentDuration;
                      }
                   }
                   return newRows;
                });

                // Now save both values to Google Sheets using handleCellSave
                let saveError = false;
                try {
                if (inPointColIndex !== -1) {
                     await handleCellSave(currentRowIndex, inPointColIndex); // handleCellSave reads value from rows state
                   }
                } catch (e) { saveError = true; console.error('Error saving In Point:', e); }

                try {
                   if (seekInColIndex !== -1) {
                     await handleCellSave(currentRowIndex, seekInColIndex); // handleCellSave reads value from rows state
                   }
                 } catch (e) { saveError = true; console.error('Error saving Seek In:', e); }

                try {
                   if (durationColIndex !== -1) {
                     await handleCellSave(currentRowIndex, durationColIndex); // handleCellSave reads value from rows state
                   }
                 } catch (e) { saveError = true; console.error('Error saving Duration:', e); }

                 if (saveError) {
                   setError('Error saving In Point, Seek In, or Duration to sheet.');
                }
              }
              handleVideoPlayerClose(); // Close dialog after saving
            }}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <ScriptManager
        open={scriptDialogOpen}
        onClose={() => setScriptDialogOpen(false)}
        onSave={handleScriptSave}
      />

      {/* YouTube Search Dialog */}
      <YouTubeSearchDialog
        open={searchDialogOpen}
        onClose={handleSearchDialogClose}
        onSelect={handleSearchDialogSelect}
        initialQuery={searchInitialQuery}
      />

      {/* Add Dialog for Editing S3 URL */}
      <Dialog
        open={editingS3UrlState !== null}
        onClose={() => {
          setEditingS3UrlState(null);
          setVideoSignedUrl('');
          setVideoLoadError(false);
          setIsLoadingSignedUrl(false);
          setS3VideoUploadFile(null); // Reset the upload file state
          setS3UploadProgress(0); // Reset the upload progress
          setDialogInputYoutubeUrl(''); // Reset the YouTube URL input for S3 dialog
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: { xs: '90vw', sm: '85vw', md: '700px' },
            margin: '10px',
            width: '100%',
            overflowX: 'hidden'
          }
        }}
      >
        <DialogTitle>
          <Box>
            <Typography variant="h6" component="div">
              S3 Video
            </Typography>
            {editingS3UrlState?.song && editingS3UrlState?.artist && (
              <Typography variant="subtitle1" component="div" sx={{ mt: 1 }}>
                {editingS3UrlState.artist} - {editingS3UrlState.song}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ 
          overflowX: 'hidden', 
          padding: '16px' 
        }}>
          {/* Video Player */}
          {editingS3UrlState?.url && (
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              backgroundColor: '#000',
              borderRadius: 1,
              mb: 2,
              px: { xs: 1, sm: 2, md: 3 }, // Reduce padding further
              py: 2,
              width: '100%',
              justifyContent: 'center',
              overflow: 'hidden', // Ensure content doesn't overflow
              boxSizing: 'border-box' // Include padding in width calculation
            }}>
              {isLoadingSignedUrl && (
                <CircularProgress sx={{ color: 'white', my: 2 }} />
              )}
              
              {!isLoadingSignedUrl && !videoLoadError && (
                <video
                  key={videoSignedUrl || editingS3UrlState.url} // Force re-render when URL changes
                  src={videoSignedUrl || editingS3UrlState.url}
                  controls
                  preload="metadata"
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    maxHeight: '60vh',
                    objectFit: 'contain',
                    height: 'auto',
                    border: '1px solid rgba(255,255,255,0.2)'
                  }}
                  
                  onLoadedMetadata={(e) => {
                    const video = e.target as HTMLVideoElement;
                    console.log('[VideoPlayer] Video loaded successfully!');
                    console.log(`[VideoPlayer] Dimensions: ${video.videoWidth}x${video.videoHeight}`);
                    console.log(`[VideoPlayer] Duration: ${video.duration}s`);
                    setVideoLoadError(false);
                  }}
                  onError={async (e) => {
                    console.error('[VideoPlayer] Error loading video:', e);
                    const video = e.target as HTMLVideoElement;
                    console.error('[VideoPlayer] Error code:', video.error?.code);
                    console.error('[VideoPlayer] Error message:', video.error?.message);
                    
                    // If we haven't tried a signed URL yet and we got an error
                    if (!videoSignedUrl && editingS3UrlState?.url) {
                      console.log('[VideoPlayer] Trying to get signed URL...');
                      setIsLoadingSignedUrl(true);
                      
                      try {
                        const response = await fetch(`/api/get-signed-vertical-url?url=${encodeURIComponent(editingS3UrlState.url)}`);
                        const data = await response.json();
                        
                        if (data.success && data.signedUrl) {
                          console.log('[VideoPlayer] Got signed URL, retrying with it');
                          setVideoSignedUrl(data.signedUrl);
                          setVideoLoadError(false);
                        } else {
                          console.error('[VideoPlayer] Failed to get signed URL:', data.message);
                          setVideoLoadError(true);
                        }
                      } catch (error) {
                        console.error('[VideoPlayer] Error getting signed URL:', error);
                        setVideoLoadError(true);
                      } finally {
                        setIsLoadingSignedUrl(false);
                      }
                    } else {
                      setVideoLoadError(true);
                    }
                  }}
                />
              )}
              
              {!isLoadingSignedUrl && videoLoadError && (
                <Typography color="error" sx={{ my: 2 }}>
                  Error loading video. Please check the URL or permissions.
                </Typography>
              )}
            </Box>
          )}
          
          {/* URL Edit Field */}
          <TextField
            label="S3 Video URL"
            value={editingS3UrlState?.url || ''}
            onChange={(e) => setEditingS3UrlState(prev => prev ? {...prev, url: e.target.value} : null)}
            fullWidth
            margin="normal"
          />
          
          {/* Conditionally render YouTube Link TextField */}
          {editingS3UrlState && !editingS3UrlState.currentYoutubeUrl && (
            <TextField
              label="YouTube Link"
              value={dialogInputYoutubeUrl}
              onChange={(e) => setDialogInputYoutubeUrl(e.target.value)}
              fullWidth
              margin="normal"
              placeholder="Enter YouTube URL (e.g., https://www.youtube.com/watch?v=...)"
              helperText="Required to enable 'Download from YouTube'"
              sx={{ mb: 2 }}
            />
          )}
          
          {/* File Upload Section */}
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              Upload New S3 Video
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 /* Added margin bottom */ }}>
              <Button
                variant="contained"
                component="label"
                startIcon={<CloudUploadIcon />}
              >
                Select File
                <input
                  type="file"
                  hidden
                  accept="video/mp4,video/quicktime"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setS3VideoUploadFile(file);
                      console.log(`[S3 Upload] Selected file: ${file.name} (${file.size} bytes)`);
                    }
                  }}
                />
              </Button>
              {/* "No file selected" Typography removed here */}
              {/* Moved "Download from YouTube" button here and added icon */}
              <Button
                variant="contained" // Changed from "outlined"
                startIcon={<CloudDownloadIcon />}
                disabled={(
                  editingS3UrlState?.currentYoutubeUrl 
                    ? false 
                    : !(dialogInputYoutubeUrl.trim() && (dialogInputYoutubeUrl.includes('youtube.com/') || dialogInputYoutubeUrl.includes('youtu.be/')))
                )}
                onClick={() => {
                  const currentUrlFromState = editingS3UrlState?.currentYoutubeUrl;
                  const inputUrlFromDialog = dialogInputYoutubeUrl;
                  const urlToUse = currentUrlFromState || inputUrlFromDialog;

                  console.log('[DownloadFromYTButton Debug]', { 
                    currentUrlFromState, 
                    inputUrlFromDialog, 
                    urlToUse 
                  });

                  if (editingS3UrlState && editingS3UrlState.row !== null && urlToUse && (urlToUse.includes('youtube.com/') || urlToUse.includes('youtu.be/'))) {
                    console.log(`[VideoManager] Navigating to UploadVideo with YT URL: ${urlToUse} for row ${editingS3UrlState.row}`);
                    
                    // Build URL with song and artist parameters
                    const params = new URLSearchParams({
                      youtubeUrl: urlToUse,
                      videoManagerRowIndex: editingS3UrlState.row.toString()
                    });
                    
                    // Add song and artist if available
                    if (editingS3UrlState.song) {
                      params.append('song', editingS3UrlState.song);
                    }
                    if (editingS3UrlState.artist) {
                      params.append('artist', editingS3UrlState.artist);
                    }
                    
                    navigate(`/upload-video?${params.toString()}`);
                    setEditingS3UrlState(null); // Close the dialog
                    setDialogInputYoutubeUrl(''); // Reset input
                  } else {
                    // This is the error path you're hitting:
                    if (!urlToUse || !(urlToUse.includes('youtube.com/') || urlToUse.includes('youtu.be/'))) {
                      enqueueSnackbar('VideoMan Dialog: Invalid or missing YouTube URL.', { variant: 'warning' });
                    } else { 
                      enqueueSnackbar('VideoMan Dialog: Row error for YouTube download.', { variant: 'error' });
                    }
                    console.warn('[VideoManager] Download from YouTube button clicked but conditions not met:', { editingS3UrlState, dialogInputYoutubeUrl });
                  }
                }}
                sx={{ paddingX: 2 }} // <--- ADD THIS LINE
              >
                Download from YouTube
              </Button>
            </Box>

            {/* Removed the old position of "Download from YouTube" button */}
            
            {s3VideoUploadFile && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleS3VideoUpload}
                  disabled={s3UploadProgress > 0 && s3UploadProgress < 100}
                >
                  Upload to S3
                </Button>
                {s3UploadProgress > 0 && (
                  <Box sx={{ width: '100%', mt: 1 }}>
                    <LinearProgress variant="determinate" value={s3UploadProgress} />
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {s3UploadProgress}% Uploaded
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingS3UrlState(null)}>Cancel</Button>
          <Button
            onClick={() => editingS3UrlState && handleS3UrlSave(editingS3UrlState.url)}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for editing/viewing Vertical URLs */}
      <Dialog
        open={editingVerticalUrlState !== null}
        onClose={() => {
          setEditingVerticalUrlState(null);
          setVideoSignedUrl('');
          setVideoLoadError(false);
          setIsLoadingSignedUrl(false);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: { xs: '90vw', sm: '85vw', md: '700px' },
            margin: '10px',
            width: '100%',
            overflowX: 'hidden'
          }
        }}
      >
        <DialogTitle>
          <Box>
            <Typography variant="h6" component="div">
              Vertical Video
            </Typography>
            {editingVerticalUrlState?.song && editingVerticalUrlState?.artist && (
              <Typography variant="subtitle1" component="div" sx={{ mt: 1 }}>
                {editingVerticalUrlState.artist} - {editingVerticalUrlState.song}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ 
          overflowX: 'hidden', 
          padding: '16px' 
        }}>
          {/* Video Player */}
          {editingVerticalUrlState?.url && (
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              backgroundColor: '#000',
              borderRadius: 1,
              mb: 2,
              px: { xs: 1, sm: 2, md: 3 }, // Reduce padding further
              py: 2,
              width: '100%',
              justifyContent: 'center',
              overflow: 'hidden', // Ensure content doesn't overflow
              boxSizing: 'border-box' // Include padding in width calculation
            }}>
              {isLoadingSignedUrl && (
                <CircularProgress sx={{ color: 'white', my: 2 }} />
              )}
              
              {!isLoadingSignedUrl && !videoLoadError && (
                <video
                  key={videoSignedUrl || editingVerticalUrlState.url} // Force re-render when URL changes
                  src={videoSignedUrl || editingVerticalUrlState.url}
                  controls
                  preload="metadata"
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    maxHeight: '60vh',
                    objectFit: 'contain',
                    height: 'auto',
                    border: '1px solid rgba(255,255,255,0.2)'
                  }}
                  
                  onLoadedMetadata={(e) => {
                    const video = e.target as HTMLVideoElement;
                    console.log('[VideoPlayer] Video loaded successfully!');
                    console.log(`[VideoPlayer] Dimensions: ${video.videoWidth}x${video.videoHeight}`);
                    console.log(`[VideoPlayer] Duration: ${video.duration}s`);
                    setVideoLoadError(false);
                  }}
                  onError={async (e) => {
                    console.error('[VideoPlayer] Error loading video:', e);
                    const video = e.target as HTMLVideoElement;
                    console.error('[VideoPlayer] Error code:', video.error?.code);
                    console.error('[VideoPlayer] Error message:', video.error?.message);
                    
                    // If we haven't tried a signed URL yet and we got an error
                    if (!videoSignedUrl && editingVerticalUrlState?.url) {
                      console.log('[VideoPlayer] Trying to get signed URL...');
                      setIsLoadingSignedUrl(true);
                      
                      try {
                        const response = await fetch(`/api/get-signed-vertical-url?url=${encodeURIComponent(editingVerticalUrlState.url)}`);
                        const data = await response.json();
                        
                        if (data.success && data.signedUrl) {
                          console.log('[VideoPlayer] Got signed URL, retrying with it');
                          setVideoSignedUrl(data.signedUrl);
                          setVideoLoadError(false);
                        } else {
                          console.error('[VideoPlayer] Failed to get signed URL:', data.message);
                          setVideoLoadError(true);
                        }
                      } catch (error) {
                        console.error('[VideoPlayer] Error getting signed URL:', error);
                        setVideoLoadError(true);
                      } finally {
                        setIsLoadingSignedUrl(false);
                      }
                    } else {
                      setVideoLoadError(true);
                    }
                  }}
                />
              )}
              
              {!isLoadingSignedUrl && videoLoadError && (
                <Typography color="error" sx={{ my: 2 }}>
                  Error loading video. Please check the URL or permissions.
                </Typography>
              )}
            </Box>
          )}
          
          {/* URL Edit Field */}
          <TextField
            label="Vertical Video URL"
            value={editingVerticalUrlState?.url || ''}
            onChange={(e) => setEditingVerticalUrlState(prev => prev ? {...prev, url: e.target.value} : null)}
            fullWidth
            margin="normal"
          />

          {/* File Upload Section */}
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              Upload New Vertical Video
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="contained"
                component="label"
                startIcon={<CloudUploadIcon />}
              >
                Select File
                <input
                  type="file"
                  hidden
                  accept="video/mp4,video/quicktime"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setVerticalVideoUploadFile(file);
                      console.log(`[Vertical Upload] Selected file: ${file.name} (${file.size} bytes)`);
                    }
                  }}
                />
              </Button>
              {/* Restored Typography for vertical file selection */}
              <Typography>
                {verticalVideoUploadFile ? verticalVideoUploadFile.name : 'No file selected'}
              </Typography>
            </Box>
            
            {verticalVideoUploadFile && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleVerticalVideoUpload}
                  disabled={verticalUploadProgress > 0 && verticalUploadProgress < 100}
                >
                  Upload to S3
                </Button>
                {verticalUploadProgress > 0 && (
                  <Box sx={{ width: '100%', mt: 1 }}>
                    <LinearProgress variant="determinate" value={verticalUploadProgress} />
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {verticalUploadProgress}% Uploaded
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingVerticalUrlState(null)}>Cancel</Button>
          <Button
            onClick={() => editingVerticalUrlState && handleVerticalUrlSave(editingVerticalUrlState.url)}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Render the Filename Confirmation Dialog */}
      {renderFileNameConfirmationDialog()}

    </Box>
  );
}; 

